import { useState, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReorderableTable, type ReorderableColumn } from "@/components/ReorderableTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Send, Loader2, Truck, Trash2, RotateCcw, FileText, TrendingUp, CheckCircle, FileEdit, Package, Download, FileCode, MessageSquare, Paperclip, X, Bell } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

type StatusPedido =
  | "pendente"
  | "em separação"
  | "para conferencia"
  | "em conferencia"
  | "em revisão"
  | "solicitar expedição"
  | "aguardando expedição"
  | "finalizado"
  | "cancelado"
  | "devolução";

const statusConfig: Record<StatusPedido, { bg: string; text: string }> = {
  "pendente":             { bg: "bg-yellow-100", text: "text-yellow-800" },
  "em separação":         { bg: "bg-blue-100",   text: "text-blue-800" },
  "para conferencia":     { bg: "bg-indigo-100", text: "text-indigo-800" },
  "em conferencia":       { bg: "bg-purple-100", text: "text-purple-800" },
  "em revisão":           { bg: "bg-orange-100", text: "text-orange-800" },
  "solicitar expedição":  { bg: "bg-cyan-100",   text: "text-cyan-800" },
  "aguardando expedição": { bg: "bg-teal-100",   text: "text-teal-800" },
  "finalizado":           { bg: "bg-green-100",  text: "text-green-800" },
  "cancelado":            { bg: "bg-red-100",    text: "text-red-800" },
  "devolução":            { bg: "bg-rose-100",   text: "text-rose-800" },
};

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig[status as StatusPedido] ?? { bg: "bg-gray-100", text: "text-gray-800" };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {status}
    </span>
  );
}

function AvisoStatusPedido({ status }: { status: string }) {
  let aviso: string;
  switch (status) {
    case "aguardando finalização":
      aviso = "Pedido já coletado";
      break;
    case "aguardando expedição":
    case "solicitar expedição":
      aviso = "Aguardando coleta";
      break;
    case "em separação":
    case "para conferencia":
    case "em conferencia":
      aviso = "Pedido em preparo";
      break;
    default:
      return null;
  }
  return <span className="ml-2 text-xs">{aviso}</span>;
}

function formatDate(d: any) {
  if (!d) return "-";
  return new Date(d.toString().includes("T") ? d : d + "T12:00:00").toLocaleDateString("pt-BR");
}

const SEM_VENDEDOR_ID = "__sem_vendedor__";

type TipoDownloadComprovanteGnre = "gnre" | "danfe" | "pedido";

type ArquivoComprovanteGnre = {
  nome: string;
  url: string;
  tipoMime?: string;
  categoria?: string;
};

function adicionarTempoUtil(inicio: number, duracao: number) {
  const inicioExpediente = 8;
  const fimExpediente = 17;
  let cursor = new Date(inicio);
  let restante = duracao;
  while (restante > 0) {
    const inicioDia = new Date(cursor);
    inicioDia.setHours(inicioExpediente, 0, 0, 0);
    const fimDia = new Date(cursor);
    fimDia.setHours(fimExpediente, 0, 0, 0);
    if (cursor.getDay() === 0 || cursor.getDay() === 6 || cursor.getTime() >= fimDia.getTime()) {
      cursor.setDate(cursor.getDate() + 1);
      cursor.setHours(inicioExpediente, 0, 0, 0);
      continue;
    }
    if (cursor.getTime() < inicioDia.getTime()) cursor = inicioDia;
    const trecho = Math.min(restante, fimDia.getTime() - cursor.getTime());
    cursor = new Date(cursor.getTime() + trecho);
    restante -= trecho;
  }
  return cursor.getTime();
}

function diferencaTempoUtil(inicio: number, fim: number) {
  const inicioExpediente = 8;
  const fimExpediente = 17;
  const sinal = fim >= inicio ? 1 : -1;
  let cursor = new Date(Math.min(inicio, fim));
  const limite = Math.max(inicio, fim);
  let total = 0;
  while (cursor.getTime() < limite) {
    const inicioDia = new Date(cursor);
    inicioDia.setHours(inicioExpediente, 0, 0, 0);
    const fimDia = new Date(cursor);
    fimDia.setHours(fimExpediente, 0, 0, 0);
    const proximoDia = new Date(cursor);
    proximoDia.setHours(24, 0, 0, 0);
    const fimTrecho = Math.min(proximoDia.getTime(), limite);
    if (cursor.getDay() !== 0 && cursor.getDay() !== 6) {
      const inicioTrecho = Math.max(cursor.getTime(), inicioDia.getTime());
      const fimUtil = Math.min(fimTrecho, fimDia.getTime());
      if (fimUtil > inicioTrecho) total += fimUtil - inicioTrecho;
    }
    cursor = new Date(fimTrecho);
  }
  return total * sinal;
}

function calcularPrazoPedido(pedido: any, agora: number) {
  if (!pedido.dataPedido || !pedido.prazoPreparo) return null;
  const [horas, minutos, segundos] = String(pedido.prazoPreparo).split(":").map(Number);
  if ([horas, minutos, segundos].some(Number.isNaN)) return null;
  const paraData = (valor: any) => new Date(String(valor).includes("T") ? valor : String(valor).replace(" ", "T")).getTime();
  const limite = adicionarTempoUtil(paraData(pedido.dataPedido), (horas * 3600 + minutos * 60 + segundos) * 1000);
  const finalizadoEm = pedido.dataAutorizacaoNfe ? paraData(pedido.dataAutorizacaoNfe) : null;
  const diferenca = diferencaTempoUtil(finalizadoEm ?? agora, limite);
  const totalMinutos = Math.floor(Math.abs(diferenca) / 60000);
  const restante = `${diferenca < 0 ? "-" : ""}${String(Math.floor(totalMinutos / 60)).padStart(2, "0")}:${String(totalMinutos % 60).padStart(2, "0")}`;
  return {
    restante,
    status: finalizadoEm ? (diferenca >= 0 ? "Finalizado no prazo" : "Finalizado em atraso") : (diferenca >= 0 ? "No prazo" : "Em atraso"),
  };
}

const COLUNAS_OPCIONAIS_PEDIDOS = [
  { chave: "numeroNfe", label: "Número da Nota" },
  { chave: "referenciaBox", label: "Referência da Importação" },
  { chave: "emitente", label: "Emitente" },
  { chave: "dataPedido", label: "Data" },
  { chave: "dataFaturamento", label: "Data de Faturamento" },
  { chave: "prazoRestante", label: "Prazo restante" },
  { chave: "statusPrazo", label: "Status prazo" },
  { chave: "destinatarioNome", label: "Destinatário" },
  { chave: "destinatarioRazao", label: "Razão Social" },
  { chave: "cidadeUf", label: "Cidade/UF" },
  { chave: "valorTotal", label: "Valor" },
  { chave: "volumes", label: "Volume" },
  { chave: "pesoBruto", label: "Peso" },
  { chave: "status", label: "Status" },
  { chave: "criadoPorNome", label: "Criado por" },
  { chave: "vendedorNome", label: "Vendedor" },
  { chave: "transportadora", label: "Transportadora" },
  { chave: "tipoFrete", label: "Tipo Frete" },
  { chave: "tipoEntrega", label: "Tipo Entrega" },
  { chave: "anotacoes", label: "Anotações" },
  { chave: "danfeVenda", label: "DANFE Venda" },
  { chave: "xmlVenda", label: "XML Venda" },
  { chave: "danfeRetorno", label: "DANFE Retorno" },
  { chave: "xmlRetorno", label: "XML Retorno" },
  { chave: "boletos", label: "Boleto(s)" },
  { chave: "gnre", label: "GNRE" },
  { chave: "gnreStatus", label: "GNRE Status" },
  { chave: "pedidoPdf", label: "Pedido" },
] as const;

const COLUNAS_PEDIDOS_STORAGE_KEY = "consultas-pedidos:colunas-visiveis:v1";

function carregarColunasVisiveis() {
  const padrao = new Set(COLUNAS_OPCIONAIS_PEDIDOS.map((coluna) => coluna.chave));
  try {
    const salvas = JSON.parse(localStorage.getItem(COLUNAS_PEDIDOS_STORAGE_KEY) || "null");
    return Array.isArray(salvas) ? new Set([...salvas, "gnreStatus", "volumes", "pesoBruto"]) : padrao;
  } catch {
    return padrao;
  }
}

export default function OrderSearch() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());
  const [tipoEntregaFilters, setTipoEntregaFilters] = useState<Set<string>>(new Set());
  const [gnreStatusFilter, setGnreStatusFilter] = useState<"todos" | "sem_gnre" | "pendente" | "processado" | "processado_pago" | "erro">("todos");
  const [filtroEntreEmitentes, setFiltroEntreEmitentes] = useState(false);
  const [emitenteIdsTemporarios, setEmitenteIdsTemporarios] = useState<Set<string>>(new Set());
  const [emitenteIdsAplicados, setEmitenteIdsAplicados] = useState<Set<string>>(new Set());
  const [vendedorIdsTemporarios, setVendedorIdsTemporarios] = useState<Set<string>>(new Set());
  const [vendedorIdsAplicados, setVendedorIdsAplicados] = useState<Set<string>>(new Set());
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [sortColumn, setSortColumn] = useState("dataPedido");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [agora, setAgora] = useState(() => Date.now());
  const [colunasVisiveis, setColunasVisiveis] = useState<Set<string>>(carregarColunasVisiveis);
  const itemsPerPage = 40;

  useEffect(() => {
    const intervalo = window.setInterval(() => setAgora(Date.now()), 60000);
    return () => window.clearInterval(intervalo);
  }, []);

  useEffect(() => {
    localStorage.setItem(COLUNAS_PEDIDOS_STORAGE_KEY, JSON.stringify(Array.from(colunasVisiveis)));
  }, [colunasVisiveis]);

  // Modal detalhes
  const [detalhesPedidoId, setDetalhesPedidoId] = useState<number | null>(null);

  // Seleção múltipla para envio de cobrança em massa
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [enviandoMassa, setEnviandoMassa] = useState(false);

  // Modal Solicitar Coleta
  const [coletaModalOpen, setColetaModalOpen] = useState(false);
  const [coletaPedidoId, setColetaPedidoId] = useState<number | null>(null);
  const [coletaEndereco, setColetaEndereco] = useState({ logradouro: "", numero: "", bairro: "", cidade: "", uf: "", cep: "", complemento: "" });
  const [coletaDataEstimada, setColetaDataEstimada] = useState("");
  const [coletaOrdem, setColetaOrdem] = useState("");
  const [coletaOrdemNA, setColetaOrdemNA] = useState(false);
  const [coletaMotorista, setColetaMotorista] = useState("");
  const [coletaMotoristaNA, setColetaMotoristaNA] = useState(false);
  const [coletaVeiculo, setColetaVeiculo] = useState("");
  const [coletaVeiculoNA, setColetaVeiculoNA] = useState(false);

  // Modal Coleta em Lote
  const [coletaLoteModalOpen, setColetaLoteModalOpen] = useState(false);
  const [coletaLotePedidoIds, setColetaLotePedidoIds] = useState<number[]>([]);
  const [coletaLoteDataEstimada, setColetaLoteDataEstimada] = useState("");
  const [coletaLoteOrdem, setColetaLoteOrdem] = useState("");
  const [coletaLoteOrdemNA, setColetaLoteOrdemNA] = useState(false);
  const [coletaLoteMotorista, setColetaLoteMotorista] = useState("");
  const [coletaLoteMotoristaNA, setColetaLoteMotoristaNA] = useState(false);
  const [coletaLoteVeiculo, setColetaLoteVeiculo] = useState("");
  const [coletaLoteVeiculoNA, setColetaLoteVeiculoNA] = useState(false);

  // Modal Relatório
  const [relatorioModalOpen, setRelatorioModalOpen] = useState(false);

  // Modal Exportar XMLs e DANFEs
  const [exportacaoFiscalModalOpen, setExportacaoFiscalModalOpen] = useState(false);
  const [exportacaoFiscalMes, setExportacaoFiscalMes] = useState(() => String(new Date().getMonth() + 1));
  const [exportacaoFiscalAno, setExportacaoFiscalAno] = useState(() => String(new Date().getFullYear()));
  const [exportandoXmlsDanfes, setExportandoXmlsDanfes] = useState(false);

  // Modal Analisar Margem
  const [margemModalOpen, setMargemModalOpen] = useState(false);
  const [margemPedidoId, setMargemPedidoId] = useState<number | null>(null);

  // Modal Finalizar Pedido
  const [finalizarModalOpen, setFinalizarModalOpen] = useState(false);
  const [confirmadoPor, setConfirmadoPor] = useState("");

  // Modal Anotações
  const [anotacoesModalOpen, setAnotacoesModalOpen] = useState(false);
  const [anotacoesPedidoId, setAnotacoesPedidoId] = useState<number | null>(null);
  const [novaAnotacao, setNovaAnotacao] = useState("");
  const [anotacaoArquivos, setAnotacaoArquivos] = useState<File[]>([]);
  const [comprovantesGnre, setComprovantesGnre] = useState<File[]>([]);
  const [mencoesSelecionadas, setMencoesSelecionadas] = useState<number[]>([]);
  const [menuMencaoAberto, setMenuMencaoAberto] = useState(false);
  const [isEnviandoAnotacao, setIsEnviandoAnotacao] = useState(false);
  const [progressoAnotacao, setProgressoAnotacao] = useState<{ atual: number; total: number } | null>(null);
  const anotacaoFileInputRef = useRef<HTMLInputElement>(null);
  const comprovanteGnreFileInputRef = useRef<HTMLInputElement>(null);
  const [notificacoesAbertas, setNotificacoesAbertas] = useState(false);

  // PDF lazy por linha (coluna Pedido)
  const [pdfPedidoId, setPdfPedidoId] = useState<number | null>(null);
  const [downloadComprovanteGnre, setDownloadComprovanteGnre] = useState<{
    pedidoId: number;
    tipo: TipoDownloadComprovanteGnre;
    comprovantes: ArquivoComprovanteGnre[];
  } | null>(null);
  const [gerandoPdfContinuo, setGerandoPdfContinuo] = useState(false);

  // Cancelamento de NF-e de venda
  const [cancelamentoNfePedido, setCancelamentoNfePedido] = useState<any | null>(null);
  const [cancelamentoNfeJustificativa, setCancelamentoNfeJustificativa] = useState("");
  const { user } = useGoogleAuth();
  const isAdmin = user?.role === "admin";

  const { data: pedidos = [], isLoading } = trpc.pedidos.listOpen.useQuery();
  const utils = trpc.useUtils();

  const statusDisponiveis = Array.from(new Set(
    pedidos.map((pedido: any) => pedido.status).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "pt-BR"));

  const tiposEntregaDisponiveis = Array.from(new Set(
    pedidos.map((pedido: any) => pedido.tipoEntrega).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "pt-BR"));

  const emitentesDisponiveis = Array.from(new Map(
    pedidos
      .filter((pedido: any) => pedido.emitenteId !== null && pedido.emitenteId !== undefined)
      .map((pedido: any) => [
        String(pedido.emitenteId),
        pedido.emitenteFantasia || pedido.emitenteNome || `Emitente #${pedido.emitenteId}`,
      ])
  ).entries())
    .map(([id, nome]) => ({ id, nome }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const todosEmitentesSelecionados = emitentesDisponiveis.length > 0 &&
    emitentesDisponiveis.every((emitente) => emitenteIdsTemporarios.has(emitente.id));

  const pedidosDosEmitentesAplicados = pedidos.filter((pedido: any) =>
    emitenteIdsAplicados.size === 0 || emitenteIdsAplicados.has(String(pedido.emitenteId))
  );

  const vendedoresDisponiveis = Array.from(new Map(
    pedidosDosEmitentesAplicados.map((pedido: any) => {
      const id = pedido.vendedorId === null || pedido.vendedorId === undefined
        ? SEM_VENDEDOR_ID
        : String(pedido.vendedorId);
      const nome = id === SEM_VENDEDOR_ID
        ? "Sem vendedor"
        : pedido.vendedorNome || `Vendedor #${pedido.vendedorId}`;
      return [id, nome];
    })
  ).entries())
    .map(([id, nome]) => ({ id, nome }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const todosVendedoresSelecionados = vendedoresDisponiveis.length > 0 &&
    vendedoresDisponiveis.every((vendedor) => vendedorIdsTemporarios.has(vendedor.id));

  // Download helper
  function handleDownloadBase64(base64: string, filename: string, mimeType: string) {
    const byteChars = atob(base64);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNums)], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  function handleDownloadBytes(bytes: Uint8Array, filename: string) {
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  function base64ParaBytes(base64: string) {
    const byteChars = atob(base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    return bytes;
  }

  async function executarDownloadNormal(tipo: TipoDownloadComprovanteGnre, pedidoId: number) {
    if (tipo === "gnre") return handleGnre(pedidoId);
    if (tipo === "danfe") return handleDanfeVenda(pedidoId);
    setPdfPedidoId(pedidoId);
  }

  async function abrirDownloadComprovanteGnre(tipo: TipoDownloadComprovanteGnre, pedidoId: number) {
    try {
      const anotacoes = await utils.pedidos.listAnotacoes.fetch({ pedidoId });
      const comprovantes = (anotacoes as any[]).flatMap((anotacao: any) => anotacao.arquivos || [])
        .filter((arquivo: ArquivoComprovanteGnre) => arquivo.categoria === "comprovante_gnre");
      if (comprovantes.length === 0) {
        await executarDownloadNormal(tipo, pedidoId);
        return;
      }
      setDownloadComprovanteGnre({ pedidoId, tipo, comprovantes });
    } catch (err: any) {
      toast.error(err.message || "Não foi possível consultar os comprovantes de GNRE.");
    }
  }

  async function adicionarComprovanteAoPdf(documento: any, arquivo: ArquivoComprovanteGnre) {
    const resposta = await fetch(arquivo.url, { credentials: "include" });
    if (!resposta.ok) throw new Error(`Não foi possível baixar o comprovante ${arquivo.nome}.`);
    const bytes = new Uint8Array(await resposta.arrayBuffer());
    const nome = arquivo.nome.toLowerCase();
    const tipoMime = (arquivo.tipoMime || "").toLowerCase();

    if (tipoMime === "application/pdf" || nome.endsWith(".pdf")) {
      const { PDFDocument } = await import("pdf-lib");
      const origem = await PDFDocument.load(bytes);
      const paginas = await documento.copyPages(origem, origem.getPageIndices());
      paginas.forEach((pagina: any) => documento.addPage(pagina));
      return true;
    }

    if (!tipoMime.startsWith("image/") && !/\.(png|jpe?g|webp|gif)$/i.test(nome)) return false;
    const imagem = await createImageBitmap(new Blob([bytes], { type: tipoMime || "image/*" }));
    const canvas = document.createElement("canvas");
    canvas.width = imagem.width;
    canvas.height = imagem.height;
    const contexto = canvas.getContext("2d");
    if (!contexto) throw new Error(`Não foi possível converter o comprovante ${arquivo.nome}.`);
    contexto.drawImage(imagem, 0, 0);
    const imagemPng = base64ParaBytes(canvas.toDataURL("image/png").split(",")[1]);
    const imagemIncorporada = await documento.embedPng(imagemPng);
    const pagina = documento.addPage([595.28, 841.89]);
    const escala = Math.min((pagina.getWidth() - 36) / imagemIncorporada.width, (pagina.getHeight() - 36) / imagemIncorporada.height);
    const largura = imagemIncorporada.width * escala;
    const altura = imagemIncorporada.height * escala;
    pagina.drawImage(imagemIncorporada, { x: (pagina.getWidth() - largura) / 2, y: (pagina.getHeight() - altura) / 2, width: largura, height: altura });
    imagem.close();
    return true;
  }

  async function gerarPdfContinuo() {
    if (!downloadComprovanteGnre) return;
    const { pedidoId, tipo, comprovantes } = downloadComprovanteGnre;
    setGerandoPdfContinuo(true);
    try {
      const { PDFDocument } = await import("pdf-lib");
      const documento = await PDFDocument.create();
      let comprovantesNaoIncluidos = 0;

      for (const comprovante of comprovantes) {
        if (!await adicionarComprovanteAoPdf(documento, comprovante)) comprovantesNaoIncluidos += 1;
      }

      const gnre = tipo === "gnre"
        ? await utils.pedidos.getGnrePdf.fetch({ pedidoId })
        : await utils.pedidos.getGnrePdf.fetch({ pedidoId }).catch(() => null);
      if (gnre) {
        const origemGnre = await PDFDocument.load(base64ParaBytes(gnre.base64));
        const paginasGnre = await documento.copyPages(origemGnre, origemGnre.getPageIndices());
        paginasGnre.forEach((pagina: any) => documento.addPage(pagina));
      }

      const danfe = tipo === "danfe"
        ? await utils.pedidos.getDanfeVendaV2.fetch({ pedidoId })
        : await utils.pedidos.getDanfeVendaV2.fetch({ pedidoId }).catch(() => null);
      if (danfe) {
        const origemDanfe = await PDFDocument.load(base64ParaBytes(danfe.base64));
        const paginasDanfe = await documento.copyPages(origemDanfe, origemDanfe.getPageIndices());
        paginasDanfe.forEach((pagina: any) => documento.addPage(pagina));
      }

      if (documento.getPageCount() === 0) throw new Error("Nenhum documento disponível para gerar o PDF contínuo.");
      handleDownloadBytes(await documento.save(), `documentos_gnre_${pedidoId}.pdf`);
      if (comprovantesNaoIncluidos > 0) toast.warning(`${comprovantesNaoIncluidos} comprovante(s) de outro formato não foi(ram) incluído(s) no PDF contínuo.`);
      setDownloadComprovanteGnre(null);
    } catch (err: any) {
      toast.error(err.message || "Não foi possível gerar o PDF contínuo.");
    } finally {
      setGerandoPdfContinuo(false);
    }
  }

  async function handleDanfeVenda(pedidoId: number) {
    try {
      const result = await utils.pedidos.getDanfeVendaV2.fetch({ pedidoId });
      handleDownloadBase64(result.base64, result.filename, 'application/pdf');
    } catch (err: any) {
      toast.error(err.message || 'DANFE de venda não encontrada.');
    }
  }

  async function handleXmlVenda(pedidoId: number) {
    try {
      const result = await utils.pedidos.getXmlVendaV2.fetch({ pedidoId });
      handleDownloadBase64(result.base64, result.filename, 'application/xml');
    } catch (err: any) {
      toast.error(err.message || 'XML de venda não encontrado.');
    }
  }

  async function handleDanfeRetorno(pedidoId: number) {
    try {
      const result = await utils.pedidos.getDanfeRetorno.fetch({ pedidoId });
      handleDownloadBase64(result.base64, result.filename, 'application/pdf');
    } catch (err: any) {
      toast.error(err.message || 'DANFE de retorno não encontrada.');
    }
  }

  async function handleXmlRetorno(pedidoId: number) {
    try {
      const result = await utils.pedidos.getXmlRetorno.fetch({ pedidoId });
      handleDownloadBase64(result.base64, result.filename, 'application/xml');
    } catch (err: any) {
      toast.error(err.message || 'XML de retorno não encontrado.');
    }
  }

  async function handleBoletos(pedidoId: number) {
    try {
      const result = await utils.pedidos.getBoletosPdf.fetch({ pedidoId });
      handleDownloadBase64(result.base64, result.filename, 'application/pdf');
    } catch (err: any) {
      toast.error(err.message || 'Nenhum boleto encontrado.');
    }
  }

  async function handleGnre(pedidoId: number) {
    try {
      const result = await utils.pedidos.getGnrePdf.fetch({ pedidoId });
      handleDownloadBase64(result.base64, result.filename, 'application/pdf');
    } catch (err: any) {
      toast.error(err.message || 'Nenhuma GNRE encontrada.');
    }
  }

  async function handleExportarXmlsDanfes() {
    const mes = Number(exportacaoFiscalMes);
    const ano = Number(exportacaoFiscalAno);
    if (!Number.isInteger(mes) || mes < 1 || mes > 12 || !Number.isInteger(ano) || ano < 2000 || ano > 2100) {
      toast.error('Selecione um mês e ano válidos.');
      return;
    }
    setExportandoXmlsDanfes(true);
    try {
      const result = await utils.pedidos.exportarXmlsDanfes.fetch({ mes, ano });
      handleDownloadBase64(result.base64, result.filename, 'application/zip');
      setExportacaoFiscalModalOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Não foi possível exportar XMLs e DANFEs.');
    } finally {
      setExportandoXmlsDanfes(false);
    }
  }

  function handleAbrirCancelamentoNfe() {
    if (selectedIds.size !== 1) {
      toast.error('Selecione exatamente um pedido para cancelar a NF-e.');
      return;
    }
    const pedidoId = Array.from(selectedIds)[0];
    const pedido = pedidos.find((item: any) => item.id === pedidoId);
    if (!pedido?.numeroNfe) {
      toast.error('O pedido selecionado não possui NF-e de venda autorizada.');
      return;
    }
    setCancelamentoNfePedido(pedido);
    setCancelamentoNfeJustificativa("");
  }

  // Detalhes do pedido (lazy — só busca quando o modal abre)
  const enviarCobrancaMutation = trpc.pedidos.enviarCobranca.useMutation({
    onSuccess: (data) => {
      if (data.aviso) {
        toast.info(data.aviso);
      } else {
        toast.success("Cobrança enviada com sucesso!");
      }
    },
    onError: (err) => toast.error(err.message || "Erro ao enviar cobrança"),
  });

  const solicitarColetaMutation = trpc.pedidos.solicitarColeta.useMutation({
    onSuccess: () => {
      toast.success("Coleta solicitada com sucesso!");
      setColetaModalOpen(false);
      setColetaPedidoId(null);
      utils.pedidos.listOpen.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao solicitar coleta"),
  });

  const solicitarColetaEmLoteMutation = trpc.pedidos.solicitarColetaEmLote.useMutation({
    onSuccess: (resultado) => {
      toast.success(`Coleta solicitada para ${resultado.pedidosAtualizados} pedidos.`);
      setColetaLoteModalOpen(false);
      setColetaLotePedidoIds([]);
      setSelectedIds(new Set());
      utils.pedidos.listOpen.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao solicitar coleta em lote"),
  });

  const excluirPedidoMutation = trpc.pedidos.excluir.useMutation({
    onSuccess: (data) => {
      toast.success(`Pedido excluído com sucesso! Agendamento de entrada criado.`);
      setSelectedIds(new Set());
    },
    onError: (err) => toast.error(err.message || "Erro ao excluir pedido"),
  });

  const estornarPedidoMutation = trpc.pedidos.estornar.useMutation({
    onSuccess: (data) => {
      toast.success(`Pedido estornado com sucesso! Agendamento de entrada criado.`);
      setSelectedIds(new Set());
    },
    onError: (err) => toast.error(err.message || "Erro ao estornar pedido"),
  });

  const enviarParaEntradaMutation = trpc.pedidos.enviarParaEntrada.useMutation({
    onSuccess: (data) => {
      toast.success(`Agendamento ${data.numeroAgendamento} criado com sucesso!`);
      utils.pedidos.listOpen.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao transformar pedido em entrada"),
  });

  const marcarComoTransferenciaMutation = trpc.pedidos.marcarComoTransferencia.useMutation({
    onSuccess: () => {
      toast.success("Pedido marcado como transferência entre emitentes.");
      utils.pedidos.listOpen.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao marcar transferência entre emitentes"),
  });

  const cancelarNfeMutation = trpc.pedidos.cancelarNfe.useMutation({
    onSuccess: (resultado) => {
      if (!resultado.success) {
        toast.error(`SEFAZ não autorizou o cancelamento (${resultado.cStat || "sem código"}): ${resultado.xMotivo || "sem motivo informado"}`);
        return;
      }
      toast.success(`NF-e ${resultado.numeroNfe} cancelada com protocolo ${resultado.protocolo || "não informado"}.`);
      setCancelamentoNfePedido(null);
      setCancelamentoNfeJustificativa("");
      setSelectedIds(new Set());
      utils.pedidos.listOpen.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao cancelar NF-e"),
  });

  const finalizarPedidoMutation = trpc.expedicao.finalizarPedido.useMutation({
    onSuccess: () => {
      toast.success("Pedido(s) finalizado(s) com sucesso!");
      setFinalizarModalOpen(false);
      setConfirmadoPor("");
      setSelectedIds(new Set());
      utils.pedidos.listOpen.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao finalizar pedido"),
  });

  const gerarRelatorioPdfMutation = trpc.pedidos.gerarRelatorioPdf.useMutation();

  async function gerarPdfPedido(pedidoId: number) {
    try {
      const result = await gerarRelatorioPdfMutation.mutateAsync({ id: pedidoId });
      const link = document.createElement("a");
      link.href = result.url;
      link.download = `pedido_${pedidoId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      toast.error(err.message || "Não foi possível gerar o PDF do pedido.");
    }
  }

  const { data: pedidoDetalhes, isLoading: loadingDetalhes } = trpc.pedidos.getDetalhes.useQuery(
    { id: detalhesPedidoId! },
    { enabled: detalhesPedidoId !== null }
  );
  const { data: stockData = [] } = trpc.products.list.useQuery(undefined, {
    enabled: detalhesPedidoId !== null,
  });
  const getDynamicStock = (productId: number | string) => {
    const stock = (stockData as any[]).find((item: any) => Number(item.id) === Number(productId));
    return stock?.quantidade ?? 0;
  };

  // Query lazy para análise de margem
  const { data: margemDetalhes, isLoading: loadingMargem } = trpc.pedidos.getDetalhes.useQuery(
    { id: margemPedidoId! },
    { enabled: margemPedidoId !== null }
  );

  // Query lazy para PDF por linha (coluna Pedido)
  const { data: pdfDetalhes } = trpc.pedidos.getDetalhes.useQuery(
    { id: pdfPedidoId! },
    { enabled: pdfPedidoId !== null }
  );

  // Query lazy para obs do orçamento no modal de anotações
  const { data: anotacoesDetalhes } = trpc.pedidos.getDetalhes.useQuery(
    { id: anotacoesPedidoId! },
    { enabled: anotacoesPedidoId !== null }
  );

  // Query de anotações do pedido
  const { data: anotacoesList = [], refetch: refetchAnotacoes } = trpc.pedidos.listAnotacoes.useQuery(
    { pedidoId: anotacoesPedidoId! },
    { enabled: anotacoesPedidoId !== null }
  );

  const { data: participantesAnotacoes = [] } = trpc.pedidos.participantesAnotacoes.useQuery(
    { pedidoId: anotacoesPedidoId! },
    { enabled: anotacoesPedidoId !== null }
  );

  const addAnotacaoMutation = trpc.pedidos.addAnotacao.useMutation({
    onSuccess: () => {
      setNovaAnotacao("");
      setAnotacaoArquivos([]);
      setComprovantesGnre([]);
      setMencoesSelecionadas([]);
      setMenuMencaoAberto(false);
      refetchAnotacoes();
      utils.pedidos.listOpen.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao adicionar anotação"),
  });
  const { data: notificacoesData } = trpc.pedidos.notificacoes.list.useQuery({ page: 1, pageSize: 20 });
  const notificacoes = (notificacoesData?.data as any[]) ?? [];
  const notificacoesNaoLidas = Number(notificacoesData?.naoLidas ?? 0);
  const marcarNotificacaoLidaMutation = trpc.pedidos.notificacoes.marcarLida.useMutation({
    onSuccess: () => utils.pedidos.notificacoes.list.invalidate(),
    onError: (err) => toast.error(err.message || "Erro ao atualizar notificação"),
  });

  const abrirAnotacoesPedido = (pedidoId: number) => {
    setAnotacoesPedidoId(pedidoId);
    setAnotacoesModalOpen(true);
    const url = new URL(window.location.href);
    url.searchParams.set("anotacoesPedido", String(pedidoId));
    window.history.replaceState(null, "", url.toString());
  };

  const fecharAnotacoesPedido = () => {
    setAnotacoesModalOpen(false);
    setAnotacoesPedidoId(null);
    setNovaAnotacao("");
    setAnotacaoArquivos([]);
    setComprovantesGnre([]);
    setMencoesSelecionadas([]);
    setMenuMencaoAberto(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("anotacoesPedido");
    window.history.replaceState(null, "", url.toString());
  };

  useEffect(() => {
    const pedidoId = Number(new URLSearchParams(window.location.search).get("anotacoesPedido"));
    if (Number.isInteger(pedidoId) && pedidoId > 0) abrirAnotacoesPedido(pedidoId);
  }, []);

  const obterSugestoesMencao = (texto: string) => {
    const correspondencia = texto.match(/(?:^|\s)@([^\s@]*)$/);
    if (!correspondencia) return [];
    const buscaMencao = correspondencia[1].toLowerCase();
    return (participantesAnotacoes as any[]).filter((participante: any) => Number(participante.id) !== Number(user?.id) && (participante.name || "").toLowerCase().includes(buscaMencao));
  };

  const inserirMencaoNoTexto = (texto: string, nome: string) => texto.replace(/@([^\s@]*)$/, `@${nome} `);

  const adicionarArquivosAnotacao = (arquivos: File[], categoria: "anexo" | "comprovante_gnre" = "anexo") => {
    const arquivoGrande = arquivos.find((arquivo) => arquivo.size > 50 * 1024 * 1024);
    if (arquivoGrande) return toast.error(`${arquivoGrande.name} excede 50 MB`);
    if (categoria === "comprovante_gnre") {
      setComprovantesGnre((atuais) => [...atuais, ...arquivos]);
      return;
    }
    setAnotacaoArquivos((atuais) => [...atuais, ...arquivos]);
  };

  const handleEnviarAnotacao = async () => {
    const totalArquivos = anotacaoArquivos.length + comprovantesGnre.length;
    if (!anotacoesPedidoId || (!novaAnotacao.trim() && totalArquivos === 0)) return;
    setIsEnviandoAnotacao(true);
    setProgressoAnotacao({ atual: 0, total: totalArquivos });
    try {
      const arquivosEnviados: Array<{ nome: string; url: string; tamanho: number; tipoMime?: string; categoria: "anexo" | "comprovante_gnre" }> = [];
      let arquivosEnviadosTotal = 0;
      const enviarArquivo = async (arquivo: File, categoria: "anexo" | "comprovante_gnre") => {
        const formData = new FormData();
        formData.append("file", arquivo);
        const resposta = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
        if (!resposta.ok) throw new Error(`Falha ao enviar ${arquivo.name}`);
        const dados = await resposta.json();
        arquivosEnviados.push({ nome: arquivo.name, url: dados.url, tamanho: arquivo.size, tipoMime: arquivo.type || undefined, categoria });
        arquivosEnviadosTotal += 1;
        setProgressoAnotacao({ atual: arquivosEnviadosTotal, total: totalArquivos });
      };
      for (const arquivo of anotacaoArquivos) {
        await enviarArquivo(arquivo, "anexo");
      }
      for (const arquivo of comprovantesGnre) {
        await enviarArquivo(arquivo, "comprovante_gnre");
      }
      const mencoes = mencoesSelecionadas.filter((id) => {
        const participante = (participantesAnotacoes as any[]).find((item: any) => Number(item.id) === Number(id));
        return participante?.name && novaAnotacao.includes(`@${participante.name}`);
      });
      await addAnotacaoMutation.mutateAsync({ pedidoId: anotacoesPedidoId, texto: novaAnotacao.trim(), mencoes, arquivos: arquivosEnviados });
    } catch (erro: any) {
      toast.error(erro?.message || "Falha no upload dos arquivos da anotação. Os arquivos permanecem prontos para nova tentativa.");
    } finally {
      setIsEnviandoAnotacao(false);
      setProgressoAnotacao(null);
    }
  };

  // Buscar detalhes do pedido para pré-preencher endereço no modal de coleta
  const { data: coletaPedidoDetalhes } = trpc.pedidos.getDetalhes.useQuery(
    { id: coletaPedidoId! },
    { enabled: coletaPedidoId !== null }
  );

  // Pré-preencher endereço quando coletaPedidoDetalhes carrega
  useEffect(() => {
    if (coletaPedidoDetalhes && coletaPedidoDetalhes.enderecoEntrega) {
      const end = typeof coletaPedidoDetalhes.enderecoEntrega === 'string'
        ? JSON.parse(coletaPedidoDetalhes.enderecoEntrega)
        : coletaPedidoDetalhes.enderecoEntrega;
      setColetaEndereco({
        logradouro: end.logradouro || "",
        numero: end.numero || "",
        bairro: end.bairro || "",
        cidade: end.cidade || "",
        uf: end.uf || "",
        cep: end.cep || "",
        complemento: end.complemento || "",
      });
    }
  }, [coletaPedidoDetalhes]);

  // Disparar PDF quando detalhes carregam (coluna Pedido)
  useEffect(() => {
    if (pdfDetalhes && pdfPedidoId !== null) {
      gerarPdfPedido(pdfDetalhes.id);
      setPdfPedidoId(null);
    }
  }, [pdfDetalhes]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [searchTerm, statusFilters, tipoEntregaFilters, gnreStatusFilter, filtroEntreEmitentes, emitenteIdsAplicados, vendedorIdsAplicados, dataInicio, dataFim]);

  function toggleStatusFilter(status: string) {
    setStatusFilters((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(status)) proximos.delete(status);
      else proximos.add(status);
      return proximos;
    });
  }

  function toggleTipoEntregaFilter(tipoEntrega: string) {
    setTipoEntregaFilters((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(tipoEntrega)) proximos.delete(tipoEntrega);
      else proximos.add(tipoEntrega);
      return proximos;
    });
  }

  function toggleSort(column: string) {
    if (sortColumn === column) {
      setSortDirection((direcao) => direcao === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
    setSelectedIds(new Set());
  }

  function getSortValue(pedido: any, column: string): string | number {
    switch (column) {
      case "numeroPedido": return Number(pedido.id || 0);
      case "numeroNfe": return Number(pedido.numeroNfe || 0);
      case "dataPedido": return pedido.dataPedido ? new Date(pedido.dataPedido.toString().includes("T") ? pedido.dataPedido : pedido.dataPedido + "T00:00:00").getTime() : 0;
      case "dataFaturamento": return pedido.dataFaturamento ? new Date(pedido.dataFaturamento.toString().includes("T") ? pedido.dataFaturamento : pedido.dataFaturamento + "T00:00:00").getTime() : 0;
      case "valorTotal": return Number(pedido.valorTotal || 0);
      case "volumes": return Number(pedido.volumes || 0);
      case "pesoBruto": return Number(pedido.pesoBruto || 0);
      case "emitente": return pedido.emitenteFantasia || pedido.emitenteNome || "";
      case "cidadeUf": return `${pedido.destinatarioCidade || ""}/${pedido.destinatarioUf || ""}`;
      case "anotacoes": return pedido.anotacoes || "";
      case "prazoRestante": return calcularPrazoPedido(pedido, agora)?.restante ?? "";
      case "statusPrazo": return calcularPrazoPedido(pedido, agora)?.status ?? "";
      case "gnreStatus": return obterChaveStatusGnre(pedido) === "sem_gnre" ? "" : obterStatusGnreExibicao(pedido);
      case "danfeVenda":
      case "xmlVenda":
      case "danfeRetorno":
      case "xmlRetorno":
      case "boletos":
      case "gnre": return pedido.numeroNfe ? 1 : 0;
      case "pedidoPdf": return Number(pedido.id || 0);
      default: return pedido[column] || "";
    }
  }

  function colunaVisivel(chave: string) {
    return !COLUNAS_OPCIONAIS_PEDIDOS.some((coluna) => coluna.chave === chave) || colunasVisiveis.has(chave);
  }

  function toggleColuna(chave: string) {
    setColunasVisiveis((anteriores) => {
      const proximas = new Set(anteriores);
      if (proximas.has(chave)) proximas.delete(chave);
      else proximas.add(chave);
      return proximas;
    });
  }

  function toggleEmitenteTemporario(emitenteId: string) {
    setEmitenteIdsTemporarios((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(emitenteId)) proximos.delete(emitenteId);
      else proximos.add(emitenteId);
      return proximos;
    });
  }

  function toggleTodosEmitentes() {
    setEmitenteIdsTemporarios(
      todosEmitentesSelecionados
        ? new Set()
        : new Set(emitentesDisponiveis.map((emitente) => emitente.id))
    );
  }

  function aplicarFiltroEmitentes() {
    const novosEmitentes = new Set(emitenteIdsTemporarios);
    const vendedoresPermitidos = new Set(pedidos
      .filter((pedido: any) => novosEmitentes.size === 0 || novosEmitentes.has(String(pedido.emitenteId)))
      .map((pedido: any) => pedido.vendedorId === null || pedido.vendedorId === undefined ? SEM_VENDEDOR_ID : String(pedido.vendedorId)));
    setEmitenteIdsAplicados(novosEmitentes);
    setVendedorIdsTemporarios((anteriores) => new Set(Array.from(anteriores).filter((id) => vendedoresPermitidos.has(id))));
    setVendedorIdsAplicados((anteriores) => new Set(Array.from(anteriores).filter((id) => vendedoresPermitidos.has(id))));
  }

  function toggleVendedorTemporario(vendedorId: string) {
    setVendedorIdsTemporarios((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(vendedorId)) proximos.delete(vendedorId);
      else proximos.add(vendedorId);
      return proximos;
    });
  }

  function toggleTodosVendedores() {
    setVendedorIdsTemporarios(
      todosVendedoresSelecionados
        ? new Set()
        : new Set(vendedoresDisponiveis.map((vendedor) => vendedor.id))
    );
  }

  function aplicarFiltroVendedores() {
    setVendedorIdsAplicados(new Set(vendedorIdsTemporarios));
  }

  function toggleSelect(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === paginatedPedidos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedPedidos.map((p: any) => p.id)));
    }
  }

  async function handleEnviarCobrancaMassa() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Enviar cobrança para ${selectedIds.size} pedido(s) selecionado(s)?`)) return;
    setEnviandoMassa(true);
    let sucesso = 0;
    const avisos: string[] = [];
    const erros: string[] = [];
    for (const pedidoId of Array.from(selectedIds)) {
      try {
        const result = await enviarCobrancaMutation.mutateAsync({ pedidoId });
        if (result.aviso) avisos.push(`#${pedidoId}: ${result.aviso}`);
        else sucesso++;
      } catch (err: any) {
        erros.push(`#${pedidoId}: ${err.message}`);
      }
    }
    setEnviandoMassa(false);
    setSelectedIds(new Set());
    if (sucesso > 0) toast.success(`${sucesso} cobrança(s) enviada(s) com sucesso.`);
    if (avisos.length > 0) avisos.forEach(a => toast.info(a));
    if (erros.length > 0) erros.forEach(e => toast.error(e));
  }

  function handleAbrirColetaModal() {
    if (selectedIds.size !== 1) {
      toast.error("Selecione exatamente 1 pedido para solicitar coleta.");
      return;
    }
    const pedidoId = Array.from(selectedIds)[0];
    const pedido = pedidos.find((p: any) => p.id === pedidoId);
    if (!pedido || pedido.status !== "solicitar expedição") {
      toast.error("O pedido selecionado não está no status 'solicitar expedição'.");
      return;
    }
    if (pedido.gnreStatus && obterChaveStatusGnre(pedido) !== "processado_pago") {
      toast.error("Pedidos com GNRE só podem solicitar coleta quando ela estiver PROCESSADO - PAGO.");
      return;
    }
    // Reset form
    setColetaPedidoId(pedidoId);
    setColetaEndereco({ logradouro: "", numero: "", bairro: "", cidade: "", uf: "", cep: "", complemento: "" });
    setColetaDataEstimada("");
    setColetaOrdem("");
    setColetaOrdemNA(false);
    setColetaMotorista("");
    setColetaMotoristaNA(false);
    setColetaVeiculo("");
    setColetaVeiculoNA(false);
    setColetaModalOpen(true);
  }

  function handleConfirmarColeta() {
    if (!coletaPedidoId) return;
    if (!coletaDataEstimada) {
      toast.error("Informe a data estimada de coleta.");
      return;
    }
    solicitarColetaMutation.mutate({
      pedidoId: coletaPedidoId,
      enderecoEntrega: coletaEndereco,
      dataEstimada: coletaDataEstimada,
      ordem: coletaOrdemNA ? null : (coletaOrdem || null),
      motorista: coletaMotoristaNA ? null : (coletaMotorista || null),
      veiculo: coletaVeiculoNA ? null : (coletaVeiculo || null),
    });
  }

  function handleExcluirPedido() {
    if (selectedIds.size !== 1) {
      toast.error("Selecione exatamente 1 pedido para excluir.");
      return;
    }
    const pedidoId = Array.from(selectedIds)[0];
    const pedido = pedidos.find((p: any) => p.id === pedidoId);
    if (!pedido) return;
    if (!confirm(`Tem certeza que deseja excluir o pedido #${pedido.id}? Isso irá cancelá-lo e criar um agendamento de entrada.`)) return;
    excluirPedidoMutation.mutate({ pedidoId });
  }

  function handleEstornarPedido() {
    if (selectedIds.size !== 1) {
      toast.error("Selecione exatamente 1 pedido para estornar.");
      return;
    }
    const pedidoId = Array.from(selectedIds)[0];
    const pedido = pedidos.find((p: any) => p.id === pedidoId);
    if (!pedido) return;
    if (!confirm(`Tem certeza que deseja estornar o pedido #${pedido.id}? Isso irá criar um agendamento de entrada para devolução.`)) return;
    estornarPedidoMutation.mutate({ pedidoId });
  }

  // Coleta requer "solicitar expedição"; pedidos com GNRE também requerem comprovante e GNRE processada.
  const podeSolicitarColetaPedido = (pedido: any) =>
    pedido?.status === "solicitar expedição" &&
    (!pedido.gnreStatus || obterChaveStatusGnre(pedido) === "processado_pago");

  const podesSolicitarColeta = selectedIds.size === 1 && (() => {
    const pedidoId = Array.from(selectedIds)[0];
    const pedido = pedidos.find((p: any) => p.id === pedidoId);
    return podeSolicitarColetaPedido(pedido);
  })();

  const podesSolicitarColetaEmLote = selectedIds.size > 1 &&
    Array.from(selectedIds).every((pedidoId) => podeSolicitarColetaPedido(pedidos.find((pedido: any) => pedido.id === pedidoId)));

  const podeTransformarEmEntrada = selectedIds.size === 1 && (() => {
    const pedido = pedidos.find((item: any) => item.id === Array.from(selectedIds)[0]);
    return !!pedido?.destinatarioEmitenteId && !pedido?.possuiEntrada;
  })();

  const podeMarcarComoTransferencia = selectedIds.size === 1 && (() => {
    const pedido = pedidos.find((item: any) => item.id === Array.from(selectedIds)[0]);
    return !!pedido?.destinatarioEmitenteId &&
      !pedido?.transferenciaEntreEmitentes &&
      ["pendente", "em separação", "para conferencia", "em conferencia", "em revisão"].includes(pedido?.status);
  })();

  function handleAbrirColetaEmLoteModal() {
    setColetaLotePedidoIds(Array.from(selectedIds));
    setColetaLoteDataEstimada("");
    setColetaLoteOrdem("");
    setColetaLoteOrdemNA(false);
    setColetaLoteMotorista("");
    setColetaLoteMotoristaNA(false);
    setColetaLoteVeiculo("");
    setColetaLoteVeiculoNA(false);
    setColetaLoteModalOpen(true);
  }

  function handleConfirmarColetaEmLote() {
    if (coletaLotePedidoIds.length < 2) return;
    if (!coletaLoteDataEstimada) {
      toast.error("Informe a data estimada de coleta.");
      return;
    }
    solicitarColetaEmLoteMutation.mutate({
      pedidoIds: coletaLotePedidoIds,
      dataEstimada: coletaLoteDataEstimada,
      ordem: coletaLoteOrdemNA ? null : (coletaLoteOrdem || null),
      motorista: coletaLoteMotoristaNA ? null : (coletaLoteMotorista || null),
      veiculo: coletaLoteVeiculoNA ? null : (coletaLoteVeiculo || null),
    });
  }

  const dataInicioTimestamp = dataInicio ? new Date(`${dataInicio}T00:00:00`).getTime() : null;
  const dataFimTimestamp = dataFim ? new Date(`${dataFim}T23:59:59.999`).getTime() : null;

  function obterChaveStatusGnre(pedido: any) {
    if (pedido.gnreStatus === "processado" && pedido.possuiComprovanteGnre) return "processado_pago";
    return pedido.gnreStatus || "sem_gnre";
  }

  function obterStatusGnreExibicao(pedido: any) {
    const statusGnre = obterChaveStatusGnre(pedido);
    return statusGnre === "processado_pago"
      ? "PROCESSADO - PAGO"
      : statusGnre === "sem_gnre"
        ? "-"
        : String(statusGnre).replaceAll("_", " ").toUpperCase();
  }

  const filteredPedidos = pedidos.filter((pedido: any) => {
    const search = searchTerm.toLowerCase();
    const correspondeBusca =
      pedido.id?.toString().includes(search) ||
      pedido.numeroNfe?.toString().includes(search) ||
      pedido.destinatarioRazao?.toLowerCase().includes(search);
    const correspondeStatus = statusFilters.size === 0 || statusFilters.has(pedido.status);
    const correspondeTipoEntrega = tipoEntregaFilters.size === 0 || tipoEntregaFilters.has(pedido.tipoEntrega);
    const correspondeGnre = gnreStatusFilter === "todos" || obterChaveStatusGnre(pedido) === gnreStatusFilter;
    const correspondeEntreEmitentes = !filtroEntreEmitentes ||
      (pedido.destinatarioEmitenteId !== null && pedido.destinatarioEmitenteId !== undefined);
    const correspondeEmitente = emitenteIdsAplicados.size === 0 ||
      emitenteIdsAplicados.has(String(pedido.emitenteId));
    const vendedorId = pedido.vendedorId === null || pedido.vendedorId === undefined
      ? SEM_VENDEDOR_ID
      : String(pedido.vendedorId);
    const correspondeVendedor = vendedorIdsAplicados.size === 0 || vendedorIdsAplicados.has(vendedorId);
    const dataPedidoTimestamp = pedido.dataPedido
      ? new Date(pedido.dataPedido.toString().includes("T") ? pedido.dataPedido : pedido.dataPedido + "T00:00:00").getTime()
      : null;
    const correspondeDataInicio = dataInicioTimestamp === null ||
      (dataPedidoTimestamp !== null && dataPedidoTimestamp >= dataInicioTimestamp);
    const correspondeDataFim = dataFimTimestamp === null ||
      (dataPedidoTimestamp !== null && dataPedidoTimestamp <= dataFimTimestamp);

    return correspondeBusca && correspondeStatus && correspondeTipoEntrega && correspondeGnre && correspondeEntreEmitentes && correspondeEmitente && correspondeVendedor && correspondeDataInicio && correspondeDataFim;
  });

  const sortedPedidos = [...filteredPedidos].sort((a: any, b: any) => {
    const valorA = getSortValue(a, sortColumn);
    const valorB = getSortValue(b, sortColumn);
    const comparacao = typeof valorA === "number" && typeof valorB === "number"
      ? valorA - valorB
      : String(valorA).localeCompare(String(valorB), "pt-BR", { numeric: true, sensitivity: "base" });
    return sortDirection === "asc" ? comparacao : -comparacao;
  });

  const totalPages = Math.ceil(sortedPedidos.length / itemsPerPage);
  const paginatedPedidos = sortedPedidos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const allPageSelected = paginatedPedidos.length > 0 && paginatedPedidos.every((p: any) => selectedIds.has(p.id));

  const pedidoColumns: ReorderableColumn<any>[] = [
    {
      id: "selecionar",
      header: <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll} className="cursor-pointer" title="Selecionar todos desta página" />,
      renderCell: (pedido) => <input type="checkbox" checked={selectedIds.has(pedido.id)} onChange={() => toggleSelect(pedido.id)} className="cursor-pointer" />,
      fixed: true,
      headerClassName: "w-10 p-2 text-left text-sm font-medium",
    },
    {
      id: "numeroPedido",
      header: "Pedido",
      renderCell: (pedido) => <span className="inline-flex items-center gap-2"><span className="text-blue-600 hover:underline cursor-pointer" onClick={() => setDetalhesPedidoId(pedido.id)}>#{pedido.id}</span>{pedido.destinatarioEmitenteId !== null && pedido.destinatarioEmitenteId !== undefined && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-800" title="CNPJ do destinatário encontrado na tabela de emitentes">Entre emitente</span>}</span>,
      className: "p-2 font-medium",
      sortKey: "numeroPedido",
    },
    ...(colunaVisivel("numeroNfe") ? [{ id: "numeroNfe", header: "Número da Nota", renderCell: (pedido: any) => pedido.numeroNfe || "-", sortKey: "numeroNfe" }] : []),
    ...(colunaVisivel("referenciaBox") ? [{ id: "referenciaBox", header: "Referência da Importação", renderCell: (pedido: any) => pedido.referenciaBox || "-", sortKey: "referenciaBox" }] : []),
    ...(colunaVisivel("emitente") ? [{ id: "emitente", header: "Emitente", renderCell: (pedido: any) => pedido.emitenteFantasia || pedido.emitenteNome || "-", sortKey: "emitente" }] : []),
    ...(colunaVisivel("dataPedido") ? [{ id: "dataPedido", header: "Data", renderCell: (pedido: any) => new Date((pedido.dataPedido || "").toString().includes("T") ? pedido.dataPedido : pedido.dataPedido + "T12:00:00").toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }), sortKey: "dataPedido" }] : []),
    ...(colunaVisivel("dataFaturamento") ? [{ id: "dataFaturamento", header: "Data de Faturamento", renderCell: (pedido: any) => formatDate(pedido.dataFaturamento), sortKey: "dataFaturamento" }] : []),
    ...(colunaVisivel("prazoRestante") ? [{ id: "prazoRestante", header: "Prazo restante", renderCell: (pedido: any) => calcularPrazoPedido(pedido, agora)?.restante ?? "-", sortKey: "prazoRestante" }] : []),
    ...(colunaVisivel("statusPrazo") ? [{ id: "statusPrazo", header: "Status prazo", renderCell: (pedido: any) => calcularPrazoPedido(pedido, agora)?.status ?? "-", sortKey: "statusPrazo" }] : []),
    ...(colunaVisivel("destinatarioNome") ? [{ id: "destinatarioNome", header: "Destinatário", renderCell: (pedido: any) => pedido.destinatarioNome || "-", sortKey: "destinatarioNome" }] : []),
    ...(colunaVisivel("destinatarioRazao") ? [{ id: "destinatarioRazao", header: "Razão Social", renderCell: (pedido: any) => pedido.destinatarioRazao || "-", sortKey: "destinatarioRazao" }] : []),
    ...(colunaVisivel("cidadeUf") ? [{ id: "cidadeUf", header: "Cidade/UF", renderCell: (pedido: any) => <>{pedido.destinatarioCidade || "-"}/{pedido.destinatarioUf || "-"}</>, sortKey: "cidadeUf" }] : []),
    ...(colunaVisivel("valorTotal") ? [{ id: "valorTotal", header: "Valor", renderCell: (pedido: any) => <>R$ {parseFloat(pedido.valorTotal || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</>, sortKey: "valorTotal" }] : []),
    ...(colunaVisivel("volumes") ? [{ id: "volumes", header: "Volume", renderCell: (pedido: any) => pedido.volumes ?? "-", sortKey: "volumes" }] : []),
    ...(colunaVisivel("pesoBruto") ? [{ id: "pesoBruto", header: "Peso", renderCell: (pedido: any) => pedido.pesoBruto ?? "-", sortKey: "pesoBruto" }] : []),
    ...(colunaVisivel("status") ? [{ id: "status", header: "Status", renderCell: (pedido: any) => <><StatusBadge status={pedido.status} /><AvisoStatusPedido status={pedido.status} /></>, sortKey: "status" }] : []),
    ...(colunaVisivel("criadoPorNome") ? [{ id: "criadoPorNome", header: "Criado por", renderCell: (pedido: any) => pedido.criadoPorNome || "-", sortKey: "criadoPorNome" }] : []),
    ...(colunaVisivel("vendedorNome") ? [{ id: "vendedorNome", header: "Vendedor", renderCell: (pedido: any) => pedido.vendedorNome || "-", sortKey: "vendedorNome" }] : []),
    ...(colunaVisivel("transportadora") ? [{ id: "transportadora", header: "Transportadora", renderCell: (pedido: any) => pedido.transportadora || "-", sortKey: "transportadora" }] : []),
    ...(colunaVisivel("tipoFrete") ? [{ id: "tipoFrete", header: "Tipo Frete", renderCell: (pedido: any) => pedido.tipoFrete || "-", sortKey: "tipoFrete" }] : []),
    ...(colunaVisivel("tipoEntrega") ? [{ id: "tipoEntrega", header: "Tipo Entrega", renderCell: (pedido: any) => pedido.tipoEntrega || "-", sortKey: "tipoEntrega" }] : []),
    ...(colunaVisivel("anotacoes") ? [{ id: "anotacoes", header: "Anotações", renderCell: (pedido: any) => <button title={pedido.possuiAnotacaoManual ? "Há anotação manual" : "Anotações"} className={`p-1 ${pedido.possuiAnotacaoManual ? "text-[#00e5ff] hover:text-[#00b8d4]" : "hover:text-blue-600"}`} onClick={() => abrirAnotacoesPedido(pedido.id)}><MessageSquare className="h-4 w-4" /></button>, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "anotacoes" }] : []),
    ...(colunaVisivel("danfeVenda") ? [{ id: "danfeVenda", header: "DANFE Venda", renderCell: (pedido: any) => <button title="Baixar DANFE de Venda" className="p-1 hover:text-blue-600" onClick={() => abrirDownloadComprovanteGnre("danfe", pedido.id)}><FileText className="h-4 w-4" /></button>, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "danfeVenda" }] : []),
    ...(colunaVisivel("xmlVenda") ? [{ id: "xmlVenda", header: "XML Venda", renderCell: (pedido: any) => <button title="Baixar XML de Venda" className="p-1 hover:text-blue-600" onClick={() => handleXmlVenda(pedido.id)}><FileCode className="h-4 w-4" /></button>, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "xmlVenda" }] : []),
    ...(colunaVisivel("danfeRetorno") ? [{ id: "danfeRetorno", header: "DANFE Retorno", renderCell: (pedido: any) => <button title="Baixar DANFE de Retorno Simbólico" className="p-1 hover:text-blue-600" onClick={() => handleDanfeRetorno(pedido.id)}><Download className="h-4 w-4" /></button>, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "danfeRetorno" }] : []),
    ...(colunaVisivel("xmlRetorno") ? [{ id: "xmlRetorno", header: "XML Retorno", renderCell: (pedido: any) => <button title="Baixar XML de Retorno Simbólico" className="p-1 hover:text-blue-600" onClick={() => handleXmlRetorno(pedido.id)}><FileCode className="h-4 w-4" /></button>, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "xmlRetorno" }] : []),
    ...(colunaVisivel("boletos") ? [{ id: "boletos", header: "Boleto(s)", renderCell: (pedido: any) => <button title="Baixar Boleto(s)" className="p-1 hover:text-blue-600" onClick={() => handleBoletos(pedido.id)}><FileText className="h-4 w-4" /></button>, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "boletos" }] : []),
    ...(colunaVisivel("gnre") ? [{ id: "gnre", header: "GNRE", renderCell: (pedido: any) => <button title="Baixar GNRE" className="p-1 hover:text-blue-600" onClick={() => abrirDownloadComprovanteGnre("gnre", pedido.id)}><Download className="h-4 w-4" /></button>, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "gnre" }] : []),
    ...(colunaVisivel("gnreStatus") ? [{ id: "gnreStatus", header: "GNRE Status", renderCell: (pedido: any) => {
      const statusGnre = obterChaveStatusGnre(pedido);
      if (statusGnre === "sem_gnre") return "-";
      const classeStatusGnre = statusGnre === "processado_pago"
        ? "bg-green-950 text-white"
        : statusGnre === "processado"
          ? "bg-green-100 text-green-800"
          : statusGnre === "pendente"
            ? "bg-yellow-100 text-yellow-800"
            : statusGnre === "erro"
              ? "bg-red-100 text-red-800"
              : "bg-slate-100 text-slate-700";
      return <span className={`rounded px-2 py-1 text-xs font-medium ${classeStatusGnre}`}>{obterStatusGnreExibicao(pedido)}</span>;
    }, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "gnreStatus" }] : []),
    ...(colunaVisivel("pedidoPdf") ? [{ id: "pedidoPdf", header: "Pedido", renderCell: (pedido: any) => <button title="Baixar PDF do Pedido" className="p-1 hover:text-blue-600" onClick={() => abrirDownloadComprovanteGnre("pedido", pedido.id)}><Download className="h-4 w-4" /></button>, className: "p-2 text-center", headerClassName: "p-2 text-center text-sm font-medium", sortKey: "pedidoPdf" }] : []),
  ];

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Consulta Pedidos
            <Button variant="outline" size="icon" className="relative ml-auto" title="Notificações de pedidos" onClick={() => setNotificacoesAbertas(true)}>
              <Bell className="h-4 w-4" />
              {notificacoesNaoLidas > 0 && <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-orange-500 px-1 text-[10px] leading-4 text-white">{notificacoesNaoLidas > 99 ? "99+" : notificacoesNaoLidas}</span>}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filtro + botão enviar cobrança em massa */}
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap rounded border bg-slate-50 p-2">
              <span className="text-sm font-medium">Busca e escopo:</span>
              <Input
                placeholder="Buscar por # do pedido, NF-e de venda ou razão social..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    {emitenteIdsAplicados.size === 0 || emitenteIdsAplicados.size === emitentesDisponiveis.length
                      ? "Todos os emitentes"
                      : `Emitentes (${emitenteIdsAplicados.size})`}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72 p-2">
                  <label className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                    <Checkbox
                      checked={todosEmitentesSelecionados}
                      onCheckedChange={toggleTodosEmitentes}
                    />
                    <span className="text-sm font-medium">Selecionar todos</span>
                  </label>
                  <div className="my-2 max-h-56 overflow-y-auto border-y py-1">
                    {emitentesDisponiveis.map((emitente) => (
                      <label key={emitente.id} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                        <Checkbox
                          checked={emitenteIdsTemporarios.has(emitente.id)}
                          onCheckedChange={() => toggleEmitenteTemporario(emitente.id)}
                        />
                        <span className="text-sm">{emitente.nome}</span>
                      </label>
                    ))}
                  </div>
                  <Button size="sm" className="w-full" onClick={aplicarFiltroEmitentes}>
                    Aplicar
                  </Button>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    {vendedorIdsAplicados.size === 0 || vendedorIdsAplicados.size === vendedoresDisponiveis.length
                      ? "Todos os vendedores"
                      : `Vendedores (${vendedorIdsAplicados.size})`}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72 p-2">
                  <label className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                    <Checkbox
                      checked={todosVendedoresSelecionados}
                      onCheckedChange={toggleTodosVendedores}
                    />
                    <span className="text-sm font-medium">Selecionar todos</span>
                  </label>
                  <div className="my-2 max-h-56 overflow-y-auto border-y py-1">
                    {vendedoresDisponiveis.map((vendedor) => (
                      <label key={vendedor.id} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                        <Checkbox
                          checked={vendedorIdsTemporarios.has(vendedor.id)}
                          onCheckedChange={() => toggleVendedorTemporario(vendedor.id)}
                        />
                        <span className="text-sm">{vendedor.nome}</span>
                      </label>
                    ))}
                  </div>
                  <Button size="sm" className="w-full" onClick={aplicarFiltroVendedores}>
                    Aplicar
                  </Button>
                </DropdownMenuContent>
              </DropdownMenu>
              <span className="text-sm font-medium">De:</span>
              <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="w-auto" />
              <span className="text-sm font-medium">Até:</span>
              <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="w-auto" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">Colunas</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 max-h-96 overflow-y-auto p-2">
                  {COLUNAS_OPCIONAIS_PEDIDOS.map((coluna) => (
                    <label key={coluna.chave} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                      <Checkbox
                        checked={colunasVisiveis.has(coluna.chave)}
                        onCheckedChange={() => toggleColuna(coluna.chave)}
                      />
                      <span className="text-sm">{coluna.label}</span>
                    </label>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2 rounded border bg-slate-50 p-2">
              <span className="text-sm font-medium">Pedido — Status:</span>
              <Button
                size="sm"
                variant={statusFilters.size === 0 ? "default" : "outline"}
                onClick={() => setStatusFilters(new Set())}
              >
                Todos
              </Button>
              {statusDisponiveis.map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilters.has(status) ? "default" : "outline"}
                  onClick={() => toggleStatusFilter(status)}
                >
                  {status}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2 rounded border bg-slate-50 p-2">
              <span className="text-sm font-medium">Pedido — Tipo de Entrega:</span>
              <Button
                size="sm"
                variant={tipoEntregaFilters.size === 0 ? "default" : "outline"}
                onClick={() => setTipoEntregaFilters(new Set())}
              >
                Todos
              </Button>
              {tiposEntregaDisponiveis.map((tipoEntrega) => (
                <Button
                  key={tipoEntrega}
                  size="sm"
                  variant={tipoEntregaFilters.has(tipoEntrega) ? "default" : "outline"}
                  onClick={() => toggleTipoEntregaFilter(tipoEntrega)}
                >
                  {tipoEntrega}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2 rounded border bg-slate-50 p-2">
              <span className="text-sm font-medium">Pedido — Entre emitentes:</span>
              <Button size="sm" variant={!filtroEntreEmitentes ? "default" : "outline"} onClick={() => setFiltroEntreEmitentes(false)}>Todos</Button>
              <Button size="sm" variant={filtroEntreEmitentes ? "default" : "outline"} onClick={() => setFiltroEntreEmitentes(true)}>Entre emitente</Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2 rounded border border-green-200 bg-green-50 p-2">
              <span className="text-sm font-medium text-green-950">GNRE:</span>
              <Button size="sm" variant={gnreStatusFilter === "todos" ? "default" : "outline"} onClick={() => setGnreStatusFilter("todos")}>Todos</Button>
              <Button size="sm" variant={gnreStatusFilter === "sem_gnre" ? "default" : "outline"} onClick={() => setGnreStatusFilter("sem_gnre")}>Sem GNRE</Button>
              <Button size="sm" variant={gnreStatusFilter === "pendente" ? "default" : "outline"} onClick={() => setGnreStatusFilter("pendente")}>Pendente</Button>
              <Button size="sm" variant={gnreStatusFilter === "processado" ? "default" : "outline"} onClick={() => setGnreStatusFilter("processado")}>Processado</Button>
              <Button size="sm" variant={gnreStatusFilter === "processado_pago" ? "default" : "outline"} onClick={() => setGnreStatusFilter("processado_pago")}>Processado - Pago</Button>
              <Button size="sm" variant={gnreStatusFilter === "erro" ? "default" : "outline"} onClick={() => setGnreStatusFilter("erro")}>Erro</Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                disabled={enviandoMassa || selectedIds.size === 0}
                onClick={handleEnviarCobrancaMassa}
              >
                {enviandoMassa
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
                  : <><Send className="h-4 w-4 mr-2" />Enviar Cobrança{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                disabled={!podesSolicitarColeta}
                onClick={handleAbrirColetaModal}
              >
                <Truck className="h-4 w-4 mr-2" />
                Solicitar Coleta
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                disabled={!podesSolicitarColetaEmLote}
                onClick={handleAbrirColetaEmLoteModal}
              >
                <Truck className="h-4 w-4 mr-2" />
                Coleta em Lote{selectedIds.size > 1 ? ` (${selectedIds.size})` : ""}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                disabled={selectedIds.size !== 1 || excluirPedidoMutation.isPending}
                onClick={handleExcluirPedido}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {excluirPedidoMutation.isPending ? "Excluindo..." : "Excluir Pedido"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                disabled={selectedIds.size !== 1 || estornarPedidoMutation.isPending}
                onClick={handleEstornarPedido}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {estornarPedidoMutation.isPending ? "Estornando..." : "Estornar Pedido"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                disabled={!podeTransformarEmEntrada || enviarParaEntradaMutation.isPending}
                onClick={() => enviarParaEntradaMutation.mutate({ pedidoId: Array.from(selectedIds)[0] })}
              >
                <Package className="h-4 w-4 mr-2" />
                {enviarParaEntradaMutation.isPending ? "Transformando..." : "Transformar em entrada"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[190px] justify-center"
                disabled={!podeMarcarComoTransferencia || marcarComoTransferenciaMutation.isPending}
                onClick={() => marcarComoTransferenciaMutation.mutate({ pedidoId: Array.from(selectedIds)[0] })}
              >
                <Package className="h-4 w-4 mr-2" />
                {marcarComoTransferenciaMutation.isPending ? "Marcando..." : "Marcar como transferência"}
              </Button>
              {isAdmin && <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50 min-w-[160px] justify-center"
                disabled={selectedIds.size !== 1 || cancelarNfeMutation.isPending}
                onClick={handleAbrirCancelamentoNfe}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {cancelarNfeMutation.isPending ? "Cancelando NF-e..." : "Cancelar NF-e"}
              </Button>}
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                onClick={() => setRelatorioModalOpen(true)}
              >
                <FileText className="h-4 w-4 mr-2" />
                Relatório
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[210px] justify-center"
                onClick={() => setExportacaoFiscalModalOpen(true)}
              >
                <FileCode className="h-4 w-4 mr-2" />
                Exportar XMLs e DANFEs
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                disabled={selectedIds.size !== 1}
                onClick={() => {
                  const id = Array.from(selectedIds)[0];
                  setMargemPedidoId(id);
                  setMargemModalOpen(true);
                }}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Analisar Margem
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 min-w-[160px] justify-center"
                disabled={selectedIds.size === 0}
                onClick={() => {
                  const ids = Array.from(selectedIds);
                  const pedidosSelecionados = filteredPedidos.filter((p: any) => ids.includes(p.id));
                  const todosAguardando = pedidosSelecionados.every((p: any) => p.status === 'aguardando finalização');
                  if (!todosAguardando) {
                    toast.error('Selecione apenas pedidos com status "aguardando finalização"');
                    return;
                  }
                  setFinalizarModalOpen(true);
                }}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Finalizar Pedido
              </Button>
            </div>
          </div>

          {isLoading && <p className="text-center text-muted-foreground">Carregando...</p>}

          {!isLoading && filteredPedidos.length === 0 && (
            <p className="text-center text-muted-foreground">
              {searchTerm || statusFilters.size > 0 || tipoEntregaFilters.size > 0 || gnreStatusFilter !== "todos" || filtroEntreEmitentes || emitenteIdsAplicados.size > 0 || vendedorIdsAplicados.size > 0 || dataInicio || dataFim
                ? "Nenhum pedido encontrado"
                : "Nenhum pedido cadastrado"}
            </p>
          )}

          {!isLoading && filteredPedidos.length > 0 && (
            <>
              <div className="relative w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden pb-2 [scrollbar-color:#f97316_#e5e7eb] [scrollbar-width:auto] [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-orange-500 [&::-webkit-scrollbar-thumb:hover]:bg-orange-600">
                <ReorderableTable
                  pageKey="order-search"
                  userId={user?.id}
                  columns={pedidoColumns}
                  rows={paginatedPedidos}
                  getRowKey={(pedido) => pedido.id}
                  getRowProps={(pedido) => ({ className: selectedIds.has(pedido.id) ? "bg-blue-50" : "" })}
                  sort={{ column: sortColumn, direction: sortDirection, onToggle: toggleSort }}
                  tableClassName="w-full text-sm"
                />
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">
                    Página {currentPage} de {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Detalhes do Pedido (olhinho)
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={detalhesPedidoId !== null} onOpenChange={(o) => { if (!o) setDetalhesPedidoId(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" style={{ width: '80vw', maxWidth: '80vw' }}>
          <DialogHeader>
            <DialogTitle>
              Detalhes do Pedido {pedidoDetalhes ? `#${pedidoDetalhes.id}` : ""}
            </DialogTitle>
          </DialogHeader>

          {loadingDetalhes && <p className="text-center text-muted-foreground py-8">Carregando...</p>}

          {pedidoDetalhes && !loadingDetalhes && (
            <div className="space-y-6">
              {/* Emitente */}
              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Emitente</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium">Razão Social:</span> {pedidoDetalhes.emitenteNome || "-"}</div>
                  <div><span className="font-medium">Nome Fantasia:</span> {pedidoDetalhes.emitenteFantasia || "-"}</div>
                  <div><span className="font-medium">CNPJ:</span> {pedidoDetalhes.emitenteCnpj || "-"}</div>
                  <div><span className="font-medium">UF:</span> {pedidoDetalhes.emitenteUf || "-"}</div>
                </div>
              </section>

              {/* Destinatário */}
              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Destinatário</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium">Razão Social:</span> {pedidoDetalhes.destinatarioRazao || "-"}</div>
                  <div><span className="font-medium">Nome Fantasia:</span> {pedidoDetalhes.destinatarioFantasia || "-"}</div>
                  <div><span className="font-medium">CNPJ:</span> {pedidoDetalhes.destinatarioCnpj || "-"}</div>
                  <div><span className="font-medium">Cidade/UF:</span> {pedidoDetalhes.destinatarioCidade || "-"} / {pedidoDetalhes.destinatarioUf || "-"}</div>
                  <div className="col-span-2">
                    <span className="font-medium">Endereço:</span>{" "}
                    {[pedidoDetalhes.destinatarioLogradouro, pedidoDetalhes.destinatarioNumero, pedidoDetalhes.destinatarioBairro, pedidoDetalhes.destinatarioCep].filter(Boolean).join(", ") || "-"}
                  </div>
                </div>
              </section>

              {/* Configuração do Orçamento */}
              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Configuração do Orçamento</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium">Tipo de Orçamento:</span> {pedidoDetalhes.tipoOrcamentoNome || "-"}</div>
                  <div><span className="font-medium">Condição de Pagamento:</span> {pedidoDetalhes.condicaoPagamentoDesc || "-"}</div>
                  <div><span className="font-medium">Tipo de Frete:</span> {pedidoDetalhes.tipoFrete || "-"}</div>
                  <div><span className="font-medium">Tipo de Entrega:</span> {pedidoDetalhes.tipoEntrega || "-"}</div>
                  <div><span className="font-medium">Transportadora:</span> {pedidoDetalhes.transportadora || "-"}</div>
                  <div><span className="font-medium">Tag:</span> {pedidoDetalhes.tag || pedidoDetalhes.orcamentoTag || "-"}</div>
                  <div><span className="font-medium">Status:</span> <StatusBadge status={pedidoDetalhes.status} /></div>
                  <div><span className="font-medium">Data:</span> {formatDate(pedidoDetalhes.criadoEm)}</div>
                </div>
              </section>

              {/* Observações */}
              {(pedidoDetalhes.obsPedidoInterno || pedidoDetalhes.obsPedidoDestinatario || pedidoDetalhes.orcamentoAnotacoes) && (
                <section>
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Observações</h3>
                  <div className="space-y-2 text-sm">
                    {pedidoDetalhes.obsPedidoInterno && (
                      <div>
                        <span className="font-medium">Obs. Interno:</span>
                        <p className="mt-1 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap">{pedidoDetalhes.obsPedidoInterno}</p>
                      </div>
                    )}
                    {pedidoDetalhes.obsPedidoDestinatario && (
                      <div>
                        <span className="font-medium">Obs. Destinatário:</span>
                        <p className="mt-1 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap">{pedidoDetalhes.obsPedidoDestinatario}</p>
                      </div>
                    )}
                    {pedidoDetalhes.orcamentoAnotacoes && (
                      <div>
                        <span className="font-medium">Anotações do Orçamento:</span>
                        <p className="mt-1 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap">{pedidoDetalhes.orcamentoAnotacoes}</p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Itens */}
              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">
                  Itens do Pedido ({(pedidoDetalhes.itens || []).length})
                </h3>
                {(pedidoDetalhes.itens || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum item cadastrado.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border px-3 py-2 text-left">Código</th>
                          <th className="border px-3 py-2 text-left">Descrição</th>
                          <th className="border px-3 py-2 text-right">Qtd.</th>
                          <th className="border px-3 py-2 text-right">Saldo Estoque</th>
                          <th className="border px-3 py-2 text-right">Cx. Master</th>
                          <th className="border px-3 py-2 text-right">Vl. Unit.</th>
                          <th className="border px-3 py-2 text-right">Total</th>
                          <th className="border px-3 py-2 text-left">Endereços</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(pedidoDetalhes.itens || []).map((item: any) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="border px-3 py-2 font-mono text-xs">{item.productCod}</td>
                            <td className="border px-3 py-2">{item.productDescription}</td>
                            <td className="border px-3 py-2 text-right">{parseFloat(item.quantidade).toFixed(0)}</td>
                            <td className="border px-3 py-2 text-right">{Math.trunc(Number(getDynamicStock(item.produtoId)))}</td>
                            <td className="border px-3 py-2 text-right">{item.qtdCaixaMaster || "-"}</td>
                            <td className="border px-3 py-2 text-right">R$ {parseFloat(item.valorUnitario).toFixed(2)}</td>
                            <td className="border px-3 py-2 text-right">R$ {parseFloat(item.valorTotal).toFixed(2)}</td>
                            <td className="border px-3 py-2 text-xs text-muted-foreground">
                              {(item.enderecos || []).length > 0 ? item.enderecos.join(", ") : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}

          <DialogFooter className="flex gap-2 justify-between sm:justify-between">
            <Button
              variant="outline"
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
              disabled={enviarCobrancaMutation.isPending}
              onClick={() => {
                if (detalhesPedidoId && confirm(`Enviar cobrança do pedido #${detalhesPedidoId} para o destinatário?`)) {
                  enviarCobrancaMutation.mutate({ pedidoId: detalhesPedidoId });
                }
              }}
            >
              <Send className="h-4 w-4 mr-2" />
              Enviar Cobrança
            </Button>
            <Button
              variant="outline"
              className="text-green-600 border-green-300 hover:bg-green-50"
              onClick={() => { if (pedidoDetalhes) gerarPdfPedido(pedidoDetalhes.id); }}
            >
              <Download className="h-4 w-4 mr-2" />
              Baixar PDF
            </Button>
            <Button variant="outline" onClick={() => setDetalhesPedidoId(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Solicitar Coleta
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={coletaModalOpen} onOpenChange={(o) => { if (!o) { setColetaModalOpen(false); setColetaPedidoId(null); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Solicitar Coleta — Pedido #{coletaPedidoId}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Endereço de Entrega */}
            <div>
              <h4 className="font-semibold text-sm mb-2">Endereço de Entrega</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground">Logradouro</label>
                  <Input value={coletaEndereco.logradouro} onChange={(e) => setColetaEndereco(prev => ({ ...prev, logradouro: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Número</label>
                  <Input value={coletaEndereco.numero} onChange={(e) => setColetaEndereco(prev => ({ ...prev, numero: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Complemento</label>
                  <Input value={coletaEndereco.complemento} onChange={(e) => setColetaEndereco(prev => ({ ...prev, complemento: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Bairro</label>
                  <Input value={coletaEndereco.bairro} onChange={(e) => setColetaEndereco(prev => ({ ...prev, bairro: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Cidade</label>
                  <Input value={coletaEndereco.cidade} onChange={(e) => setColetaEndereco(prev => ({ ...prev, cidade: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">UF</label>
                  <Input value={coletaEndereco.uf} onChange={(e) => setColetaEndereco(prev => ({ ...prev, uf: e.target.value }))} maxLength={2} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">CEP</label>
                  <Input value={coletaEndereco.cep} onChange={(e) => setColetaEndereco(prev => ({ ...prev, cep: e.target.value }))} />
                </div>
              </div>
            </div>

            {/* Data Estimada */}
            <div>
              <label className="text-xs text-muted-foreground">Data Estimada de Coleta *</label>
              <Input type="date" value={coletaDataEstimada} onChange={(e) => setColetaDataEstimada(e.target.value)} />
            </div>

            {/* Ordem de Coleta */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="text-xs text-muted-foreground">Ordem de Coleta</label>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={coletaOrdemNA} onChange={(e) => setColetaOrdemNA(e.target.checked)} />
                  Não se aplica
                </label>
              </div>
              <Input value={coletaOrdem} onChange={(e) => setColetaOrdem(e.target.value)} disabled={coletaOrdemNA} placeholder={coletaOrdemNA ? "N/A" : "Número/código da ordem de coleta"} />
            </div>

            {/* Dados do Motorista */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="text-xs text-muted-foreground">Dados do Motorista</label>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={coletaMotoristaNA} onChange={(e) => setColetaMotoristaNA(e.target.checked)} />
                  Não se aplica
                </label>
              </div>
              <Input value={coletaMotorista} onChange={(e) => setColetaMotorista(e.target.value)} disabled={coletaMotoristaNA} placeholder={coletaMotoristaNA ? "N/A" : "Nome, CNH, telefone..."} />
            </div>

            {/* Dados do Veículo */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="text-xs text-muted-foreground">Dados do Veículo</label>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={coletaVeiculoNA} onChange={(e) => setColetaVeiculoNA(e.target.checked)} />
                  Não se aplica
                </label>
              </div>
              <Input value={coletaVeiculo} onChange={(e) => setColetaVeiculo(e.target.value)} disabled={coletaVeiculoNA} placeholder={coletaVeiculoNA ? "N/A" : "Placa, modelo..."} />
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => { setColetaModalOpen(false); setColetaPedidoId(null); }}>Cancelar</Button>
            <Button
              onClick={handleConfirmarColeta}
              disabled={solicitarColetaMutation.isPending}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {solicitarColetaMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processando...</> : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Coleta em Lote
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={coletaLoteModalOpen} onOpenChange={(open) => { if (!open) { setColetaLoteModalOpen(false); setColetaLotePedidoIds([]); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Coleta em Lote — {coletaLotePedidoIds.length} pedidos</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Os endereços já salvos em cada pedido serão preservados.</p>
            <div>
              <label className="text-xs text-muted-foreground">Data Estimada de Coleta *</label>
              <Input type="date" value={coletaLoteDataEstimada} onChange={(e) => setColetaLoteDataEstimada(e.target.value)} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="text-xs text-muted-foreground">Ordem de Coleta</label>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={coletaLoteOrdemNA} onChange={(e) => setColetaLoteOrdemNA(e.target.checked)} />
                  Não se aplica
                </label>
              </div>
              <Input value={coletaLoteOrdem} onChange={(e) => setColetaLoteOrdem(e.target.value)} disabled={coletaLoteOrdemNA} placeholder={coletaLoteOrdemNA ? "N/A" : "Número/código da ordem de coleta"} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="text-xs text-muted-foreground">Dados do Motorista</label>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={coletaLoteMotoristaNA} onChange={(e) => setColetaLoteMotoristaNA(e.target.checked)} />
                  Não se aplica
                </label>
              </div>
              <Input value={coletaLoteMotorista} onChange={(e) => setColetaLoteMotorista(e.target.value)} disabled={coletaLoteMotoristaNA} placeholder={coletaLoteMotoristaNA ? "N/A" : "Nome, CNH, telefone..."} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="text-xs text-muted-foreground">Dados do Veículo</label>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={coletaLoteVeiculoNA} onChange={(e) => setColetaLoteVeiculoNA(e.target.checked)} />
                  Não se aplica
                </label>
              </div>
              <Input value={coletaLoteVeiculo} onChange={(e) => setColetaLoteVeiculo(e.target.value)} disabled={coletaLoteVeiculoNA} placeholder={coletaLoteVeiculoNA ? "N/A" : "Placa, modelo..."} />
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => { setColetaLoteModalOpen(false); setColetaLotePedidoIds([]); }}>Cancelar</Button>
            <Button
              onClick={handleConfirmarColetaEmLote}
              disabled={solicitarColetaEmLoteMutation.isPending}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {solicitarColetaEmLoteMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processando...</> : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Relatório
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={relatorioModalOpen} onOpenChange={setRelatorioModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Gerar Relatório</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Gerar relatório dos <strong>{filteredPedidos.length}</strong> pedidos filtrados em tela (ignora paginação).
          </p>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => {
              // Gerar Excel (xlsx)
              import('xlsx').then(XLSX => {
                const rows = filteredPedidos.map((p: any) => ({
                  Número: `#${p.id}`,
                  'Número da Nota': p.numeroNfe || '-',
                  'Referência da Importação': p.referenciaBox || '-',
                  Emitente: p.emitenteFantasia || p.emitenteNome || '-',
                  Data: p.dataPedido ? new Date(p.dataPedido.toString().includes('T') ? p.dataPedido : p.dataPedido + 'T12:00:00').toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-',
                  'Data de Faturamento': formatDate(p.dataFaturamento),
                  'Prazo restante': calcularPrazoPedido(p, agora)?.restante ?? '-',
                  'Status prazo': calcularPrazoPedido(p, agora)?.status ?? '-',
                  Destinatário: p.destinatarioNome || '-',
                  'Razão Social': p.destinatarioRazao || '-',
                  'Cidade/UF': `${p.destinatarioCidade || '-'}/${p.destinatarioUf || '-'}`,
                  'Valor (R$)': parseFloat(p.valorTotal || 0).toFixed(2),
                  Status: p.status || '-',
                  'Criado por': p.criadoPorNome || '-',
                  Vendedor: p.vendedorNome || '-',
                  Transportadora: p.transportadora || '-',
                  'Tipo Frete': p.tipoFrete || '-',
                  'Tipo Entrega': p.tipoEntrega || '-',
                  'Entre emitente': p.destinatarioEmitenteId !== null && p.destinatarioEmitenteId !== undefined ? 'Sim' : 'Não',
                  Anotações: p.possuiAnotacaoManual ? 'Sim' : 'Não',
                  'DANFE Venda': p.numeroNfe ? 'Sim' : 'Não',
                  'XML Venda': p.numeroNfe ? 'Sim' : 'Não',
                  'DANFE Retorno': p.possuiDanfeRetorno ? 'Sim' : 'Não',
                  'XML Retorno': p.possuiXmlRetorno ? 'Sim' : 'Não',
                  'Boleto(s)': p.possuiBoletos ? 'Sim' : 'Não',
                  GNRE: p.possuiComprovanteGnre || p.possuiGnrePdf ? 'Sim' : 'Não',
                  'GNRE Status': obterStatusGnreExibicao(p),
                  Pedido: 'Sim',
                }));
                const ws = XLSX.utils.json_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Pedidos');
                XLSX.writeFile(wb, `relatorio_pedidos_${new Date().toISOString().slice(0,10)}.xlsx`);
                setRelatorioModalOpen(false);
              });
            }}>
              <Download className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button onClick={() => {
              // Gerar PDF com jsPDF
              const doc = new jsPDF({ orientation: 'landscape' });
              const lm = 10;
              let y = 15;
              doc.setFontSize(12); doc.setFont('helvetica', 'bold');
              doc.text(`Relatório de Pedidos — ${filteredPedidos.length} registros`, lm, y); y += 8;
              doc.setFontSize(7); doc.setFont('helvetica', 'bold');
              const cols = ['Nº', 'Data', 'Destinatário', 'Cidade/UF', 'Valor', 'Status', 'Criado por', 'Vendedor', 'Transportadora', 'T.Frete', 'T.Entrega'];
              const colW = [12, 18, 40, 22, 22, 28, 28, 28, 30, 18, 18];
              let x = lm;
              cols.forEach((c, i) => { doc.text(c, x, y); x += colW[i]; }); y += 5;
              doc.setFont('helvetica', 'normal');
              filteredPedidos.forEach((p: any) => {
                if (y > 190) { doc.addPage(); y = 15; }
                x = lm;
                const vals = [
                  `#${p.id || '-'}`,
                  p.dataPedido ? new Date(p.dataPedido.toString().includes('T') ? p.dataPedido : p.dataPedido + 'T12:00:00').toLocaleDateString('pt-BR') : '-',
                  String(p.destinatarioNome || '-').slice(0, 22),
                  `${p.destinatarioCidade || '-'}/${p.destinatarioUf || '-'}`,
                  `R$ ${parseFloat(p.valorTotal || 0).toFixed(2)}`,
                  String(p.status || '-').slice(0, 16),
                  String(p.criadoPorNome || '-').slice(0, 16),
                  String(p.vendedorNome || '-').slice(0, 16),
                  String(p.transportadora || '-').slice(0, 16),
                  String(p.tipoFrete || '-').slice(0, 10),
                  String(p.tipoEntrega || '-').slice(0, 10),
                ];
                vals.forEach((v, i) => { doc.text(v, x, y); x += colW[i]; });
                y += 5;
              });
              doc.save(`relatorio_pedidos_${new Date().toISOString().slice(0,10)}.pdf`);
              setRelatorioModalOpen(false);
            }}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" onClick={() => setRelatorioModalOpen(false)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={exportacaoFiscalModalOpen} onOpenChange={(open) => { if (!exportandoXmlsDanfes) setExportacaoFiscalModalOpen(open); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Exportar XMLs e DANFEs</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            O ZIP será separado por emitente, com XMLs, DANFEs e uma relação de notas em Excel dentro de cada pasta.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-sm font-medium">
              Mês
              <select value={exportacaoFiscalMes} onChange={(event) => setExportacaoFiscalMes(event.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {[
                  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
                ].map((nome, indice) => <option key={nome} value={String(indice + 1)}>{nome}</option>)}
              </select>
            </label>
            <label className="space-y-1 text-sm font-medium">
              Ano
              <Input type="number" min="2000" max="2100" value={exportacaoFiscalAno} onChange={(event) => setExportacaoFiscalAno(event.target.value)} />
            </label>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" disabled={exportandoXmlsDanfes} onClick={() => setExportacaoFiscalModalOpen(false)}>Cancelar</Button>
            <Button disabled={exportandoXmlsDanfes} onClick={handleExportarXmlsDanfes}>
              {exportandoXmlsDanfes ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando ZIP...</> : <><Download className="h-4 w-4 mr-2" />Baixar ZIP</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Análise de Margem
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={margemModalOpen} onOpenChange={(o) => { if (!o) { setMargemModalOpen(false); setMargemPedidoId(null); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" style={{ width: '80vw', maxWidth: '80vw' }}>
          <DialogHeader>
            <DialogTitle>Análise de Margem — Pedido #{margemPedidoId}</DialogTitle>
          </DialogHeader>
          {loadingMargem && <p className="text-center text-muted-foreground py-8">Carregando...</p>}
          {margemDetalhes && !loadingMargem && (() => {
            const itens = margemDetalhes.itens || [];
            let totalVenda = 0; let totalCusto = 0;
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border px-3 py-2 text-left">Produto</th>
                      <th className="border px-3 py-2 text-right">Venda</th>
                      <th className="border px-3 py-2 text-right">Custo</th>
                      <th className="border px-3 py-2 text-right">Margem</th>
                      <th className="border px-3 py-2 text-right">Margem %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((item: any) => {
                      const venda = Number(item.valorTotal || 0);
                      const custo = Number(item.custo || 0) * Number(item.quantidade || 0);
                      const margem = venda - custo;
                      const margemPct = venda > 0 ? (margem / venda) * 100 : 0;
                      totalVenda += venda; totalCusto += custo;
                      return (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="border px-3 py-2">{item.productDescription || item.productCod || '-'}</td>
                          <td className="border px-3 py-2 text-right">R$ {venda.toFixed(2)}</td>
                          <td className="border px-3 py-2 text-right">R$ {custo.toFixed(2)}</td>
                          <td className="border px-3 py-2 text-right">R$ {margem.toFixed(2)}</td>
                          <td className="border px-3 py-2 text-right">{margemPct.toFixed(2)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold">
                      <td className="border px-3 py-2">TOTAL</td>
                      <td className="border px-3 py-2 text-right">R$ {totalVenda.toFixed(2)}</td>
                      <td className="border px-3 py-2 text-right">R$ {totalCusto.toFixed(2)}</td>
                      <td className="border px-3 py-2 text-right">R$ {(totalVenda - totalCusto).toFixed(2)}</td>
                      <td className="border px-3 py-2 text-right">{totalVenda > 0 ? (((totalVenda - totalCusto) / totalVenda) * 100).toFixed(2) : '0.00'}%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setMargemModalOpen(false); setMargemPedidoId(null); }}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Anotações do Pedido
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={anotacoesModalOpen} onOpenChange={(o) => { if (!o) fecharAnotacoesPedido(); }}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anotações — Pedido #{anotacoesPedidoId}</DialogTitle>
          </DialogHeader>
          {/* Obs do orçamento — somente leitura */}
          {anotacoesDetalhes && (anotacoesDetalhes.orcamentoAnotacoes || anotacoesDetalhes.obsPedidoInterno || anotacoesDetalhes.obsPedidoDestinatario) && (
            <div className="mb-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Obs do Orçamento</p>
              {anotacoesDetalhes.orcamentoAnotacoes && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                  <p className="text-xs font-medium text-yellow-700 mb-1">Anotações</p>
                  <p className="whitespace-pre-wrap">{anotacoesDetalhes.orcamentoAnotacoes}</p>
                </div>
              )}
              {anotacoesDetalhes.obsPedidoInterno && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                  <p className="text-xs font-medium text-yellow-700 mb-1">Obs Interno</p>
                  <p className="whitespace-pre-wrap">{anotacoesDetalhes.obsPedidoInterno}</p>
                </div>
              )}
              {anotacoesDetalhes.obsPedidoDestinatario && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                  <p className="text-xs font-medium text-yellow-700 mb-1">Obs Destinatário</p>
                  <p className="whitespace-pre-wrap">{anotacoesDetalhes.obsPedidoDestinatario}</p>
                </div>
              )}
            </div>
          )}
          {anotacoesDetalhes?.anotacoes && (
            <div className="mb-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Histórico do Pedido</p>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                <p className="whitespace-pre-wrap">{anotacoesDetalhes.anotacoes}</p>
              </div>
            </div>
          )}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Anotações do Pedido</p>
            {anotacoesList.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhuma anotação ainda.</p>
            )}
            {anotacoesList.map((a: any) => (
              <div key={a.id} className="p-3 bg-gray-50 rounded text-sm">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>{a.autorNome}</span>
                  <span>{new Date(a.criadoEm).toLocaleString('pt-BR')}</span>
                </div>
                <p className="whitespace-pre-wrap">{a.texto}</p>
                {(a.mencoes ?? []).length > 0 && <p className="mt-2 text-xs text-muted-foreground">Mencionados: {(a.mencoes ?? []).map((m: any) => `@${m.usuarioNome || "usuário"}`).join(", ")}</p>}
                {(a.arquivos ?? []).length > 0 && <div className="mt-3 flex flex-wrap gap-2">{(a.arquivos ?? []).map((arquivo: any) => <a key={arquivo.id} href={arquivo.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded border bg-white px-2 py-1 text-xs hover:bg-slate-50"><Paperclip className="h-3 w-3" />{arquivo.nome}{arquivo.categoria === "comprovante_gnre" && <span className="rounded bg-green-950 px-1.5 py-0.5 text-[10px] font-medium text-white">Comprovante de GNRE</span>}</a>)}</div>}
              </div>
            ))}
          </div>
          <div className="mt-4 rounded border p-3 space-y-2">
            <Textarea
              placeholder="Nova anotação... Use @ para marcar uma pessoa"
              value={novaAnotacao}
              onChange={(e) => { setNovaAnotacao(e.target.value); setMenuMencaoAberto(obterSugestoesMencao(e.target.value).length > 0); }}
              onPaste={(e) => { const arquivos = Array.from(e.clipboardData.files); if (arquivos.length) adicionarArquivosAnotacao(arquivos); }}
              rows={3}
            />
            {menuMencaoAberto && obterSugestoesMencao(novaAnotacao).length > 0 && <div className="rounded border bg-white p-1 shadow-sm">{obterSugestoesMencao(novaAnotacao).map((participante: any) => <button key={participante.id} type="button" className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100" onClick={() => { setNovaAnotacao((texto) => inserirMencaoNoTexto(texto, participante.name)); setMencoesSelecionadas((ids) => ids.includes(participante.id) ? ids : [...ids, participante.id]); setMenuMencaoAberto(false); }}>{participante.name || "Usuário"}{participante.contextoMencao ? ` - ${participante.contextoMencao}` : ""}</button>)}</div>}
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => anotacaoFileInputRef.current?.click()}><Paperclip className="mr-1 h-3.5 w-3.5" />Anexar</Button>
              <input ref={anotacaoFileInputRef} type="file" multiple className="hidden" onChange={(e) => { adicionarArquivosAnotacao(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
              <Button type="button" variant="outline" size="sm" className="border-green-900 text-green-950 hover:bg-green-50" onClick={() => comprovanteGnreFileInputRef.current?.click()}><Paperclip className="mr-1 h-3.5 w-3.5" />Comprovante de GNRE</Button>
              <input ref={comprovanteGnreFileInputRef} type="file" multiple className="hidden" onChange={(e) => { adicionarArquivosAnotacao(Array.from(e.target.files ?? []), "comprovante_gnre"); e.target.value = ""; }} />
              {progressoAnotacao && <span className="text-xs text-muted-foreground">Enviando {progressoAnotacao.atual}/{progressoAnotacao.total}</span>}
            </div>
            {anotacaoArquivos.length > 0 && <div className="flex flex-wrap gap-2">{anotacaoArquivos.map((arquivo, indice) => <button key={`${arquivo.name}-${indice}`} type="button" className="rounded border bg-slate-50 px-2 py-1 text-xs" onClick={() => setAnotacaoArquivos((atuais) => atuais.filter((_, index) => index !== indice))}>{arquivo.name} <X className="inline h-3 w-3" /></button>)}</div>}
            {comprovantesGnre.length > 0 && <div className="flex flex-wrap gap-2">{comprovantesGnre.map((arquivo, indice) => <button key={`${arquivo.name}-${indice}`} type="button" className="rounded border border-green-900 bg-green-50 px-2 py-1 text-xs text-green-950" onClick={() => setComprovantesGnre((atuais) => atuais.filter((_, index) => index !== indice))}>Comprovante de GNRE: {arquivo.name} <X className="inline h-3 w-3" /></button>)}</div>}
            <Button
              className="w-full"
              disabled={(!novaAnotacao.trim() && !anotacaoArquivos.length && !comprovantesGnre.length) || isEnviandoAnotacao}
              onClick={handleEnviarAnotacao}
            >
              {isEnviandoAnotacao ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</> : 'Adicionar Anotação'}
            </Button>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={fecharAnotacoesPedido}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={downloadComprovanteGnre !== null} onOpenChange={(open) => { if (!open && !gerandoPdfContinuo) setDownloadComprovanteGnre(null); }}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Comprovante de GNRE encontrado</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Este pedido possui <strong>{downloadComprovanteGnre?.comprovantes.length || 0} comprovante(s)</strong> de GNRE.
            </p>
            <p className="text-muted-foreground">
              O PDF contínuo segue a ordem: comprovante(s), GNRE e DANFE. PDFs e imagens entram no arquivo; outros formatos são ignorados.
            </p>
            {downloadComprovanteGnre?.tipo === "pedido" && <p className="text-muted-foreground">O PDF do Pedido não entra no PDF contínuo.</p>}
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button
              variant="outline"
              disabled={gerandoPdfContinuo}
              onClick={async () => {
                const download = downloadComprovanteGnre;
                setDownloadComprovanteGnre(null);
                if (download) await executarDownloadNormal(download.tipo, download.pedidoId);
              }}
            >
              Baixar somente {downloadComprovanteGnre?.tipo === "pedido" ? "Pedido" : downloadComprovanteGnre?.tipo === "danfe" ? "DANFE" : "GNRE"}
            </Button>
            <Button disabled={gerandoPdfContinuo} onClick={gerarPdfContinuo}>
              {gerandoPdfContinuo ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando...</> : "Baixar PDF contínuo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={notificacoesAbertas} onOpenChange={setNotificacoesAbertas}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Notificações de Pedidos</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {notificacoes.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma notificação.</p> : notificacoes.map((notificacao: any) => (
              <button key={notificacao.id} className={`w-full rounded-lg border p-3 text-left text-sm ${notificacao.lida ? "bg-white" : "border-orange-200 bg-orange-50"}`} onClick={() => { marcarNotificacaoLidaMutation.mutate({ id: notificacao.id }); setNotificacoesAbertas(false); abrirAnotacoesPedido(Number(notificacao.pedidoId)); }}>
                <strong className="block truncate">{notificacao.titulo}</strong>
                {notificacao.mensagem && <span className="mt-1 block text-xs text-muted-foreground">{notificacao.mensagem}</span>}
                <small className="mt-1 block text-xs text-muted-foreground">{new Date(notificacao.criadoEm).toLocaleString("pt-BR")}</small>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelamentoNfePedido} onOpenChange={(open) => {
        if (!open && !cancelarNfeMutation.isPending) {
          setCancelamentoNfePedido(null);
          setCancelamentoNfeJustificativa("");
        }
      }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Cancelar NF-e de venda</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Esta ação transmite o evento de cancelamento para a SEFAZ. O pedido, boletos, GNRE, retorno simbólico e estoque não serão alterados.
            </p>
            <div className="rounded border bg-muted/40 p-3 text-sm space-y-1">
              <p><strong>Pedido:</strong> #{cancelamentoNfePedido?.id}</p>
              <p><strong>NF-e:</strong> {cancelamentoNfePedido?.numeroNfe || "-"}</p>
              <p><strong>Valor do pedido:</strong> R$ {parseFloat(cancelamentoNfePedido?.valorTotal || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <label className="text-sm font-medium">Justificativa do cancelamento <span className="text-red-500">*</span></label>
              <Textarea
                className="mt-1"
                rows={4}
                maxLength={255}
                placeholder="Informe a justificativa exigida pela SEFAZ (15 a 255 caracteres)."
                value={cancelamentoNfeJustificativa}
                onChange={(event) => setCancelamentoNfeJustificativa(event.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">{cancelamentoNfeJustificativa.trim().length}/255 caracteres</p>
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button
              variant="outline"
              disabled={cancelarNfeMutation.isPending}
              onClick={() => {
                setCancelamentoNfePedido(null);
                setCancelamentoNfeJustificativa("");
              }}
            >
              Voltar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={cancelarNfeMutation.isPending || cancelamentoNfeJustificativa.trim().length < 15}
              onClick={() => {
                if (!cancelamentoNfePedido) return;
                cancelarNfeMutation.mutate({
                  pedidoId: cancelamentoNfePedido.id,
                  justificativa: cancelamentoNfeJustificativa.trim(),
                });
              }}
            >
              {cancelarNfeMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Transmitindo...</> : "Confirmar cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Finalizar Pedido
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={finalizarModalOpen} onOpenChange={(o) => { if (!o) { setFinalizarModalOpen(false); setConfirmadoPor(""); } }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Finalizar Pedido</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {selectedIds.size} pedido(s) selecionado(s) serão marcados como <strong>finalizado</strong>.
          </p>
          <div className="mt-4">
            <label className="text-sm font-medium">Confirmado por <span className="text-red-500">*</span></label>
            <Input
              className="mt-1"
              placeholder="Nome de quem confirma"
              value={confirmadoPor}
              onChange={(e) => setConfirmadoPor(e.target.value)}
            />
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button
              disabled={!confirmadoPor.trim() || finalizarPedidoMutation.isPending}
              onClick={() => {
                finalizarPedidoMutation.mutate({
                  pedidoIds: Array.from(selectedIds),
                  confirmadoPor: confirmadoPor.trim(),
                });
              }}
            >
              {finalizarPedidoMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Finalizando...</> : 'Confirmar'}
            </Button>
            <Button variant="outline" onClick={() => { setFinalizarModalOpen(false); setConfirmadoPor(""); }}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
